import axios from "axios";
import React, { useEffect, useRef, useState, useContext, useMemo } from "react";
import PCModal from "../modal/PC/PCModal";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import HomeIcon from "@mui/icons-material/Home";
import SendIcon from "@mui/icons-material/Send";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import Tooltip from "@mui/material/Tooltip";
import Swal from "sweetalert2";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { AppContext } from '../AppStore';
import OrderRegModal from "../modal/PC/order/OrderRegModal";
import OutgoingRegModal from "../modal/PC/order/OutgoingRegModal";
import PurchaseRegModal from "../modal/PC/purchase/PurchaseRegModal";
import IncomingRegModal from "../modal/PC/purchase/IncomingRegModal";
/**
 * ✅ (통합 확정) 요구사항 반영
 * - Android WebView: 네이티브 STT/TTS 브릿지(window.Android.*)
 * - iPhone/PC Web: Web Speech API (SpeechRecognition / speechSynthesis)
 * - TTS 재생 중 STT 금지
 * - 입력이 비어있으면 STT 자동 대기(웹/앱 포함)
 * - "말하고 난 후 입력폼 반영 → 잠깐 대기(변경 없으면) → 자동 전송"
 * - 마이크로 전송한 질문이면 답변 자동 TTS(웹/앱 포함)
 * - PC 모달에서 입력영역이 항상 맨 아래 오도록(모달만) 레이아웃 고정
 * - 위치: React에서 사용할 수 있게 저장만(geoRef.current). 필요 시 payload에 붙여 사용.
 * - ✅ 추가: 상단 제목 오른쪽에 "시/군"까지만 표시 (예: 평택시)
 * - 꾹 누르면(롱프레스) 상세주소 툴팁 노출
 */

/**
 * ✅ 코드/번호 TTS 매핑 (확정본)
 * 생산작업번호 : W / 분실번호 : L / 폐기번호 : D / 재고조정번호 : S /
 * 발주번호 : P / 입고번호 : I / 수주번호 : O / 출하번호 : R /
 * 자원번호 : M / 품질측정번호 : Q / 프로젝트 번호 : F
 */
const CODE_PREFIX = {
  W: { label: "생산작업번호" },
  L: { label: "분실번호" },
  D: { label: "폐기번호" },
  S: { label: "재고조정번호" },
  P: { label: "발주번호" },
  I: { label: "입고번호" },
  O: { label: "수주번호" },
  R: { label: "출하번호" },
  M: { label: "자원번호" },
  Q: { label: "품질측정번호" },
  F: { label: "프로젝트번호" },
};

// ✅ 채팅 유지(마이크/스피커/사파리에서 리마운트돼도 안날아가게)
// ✅ 위치 라벨 캐시 키(시/군)
const buildGeoLabelStorageKey = () => {
  try {
    const companyId = localStorage.getItem("company_id") ?? "0";
    const path =
      typeof window !== "undefined" ? window.location.pathname : "unknown";
    return `diff_chat_geo_label_v1:${companyId}:${path}`;
  } catch (e) {
    return "diff_chat_geo_label_v1";
  }
};

// ✅ 위치 상세주소 캐시 키(display_name)
const buildGeoFullStorageKey = () => {
  try {
    const companyId = localStorage.getItem("company_id") ?? "0";
    const path =
      typeof window !== "undefined" ? window.location.pathname : "unknown";
    return `diff_chat_geo_full_v1:${companyId}:${path}`;
  } catch (e) {
    return "diff_chat_geo_full_v1";
  }
};

// =====================
// ✅ Android 브릿지 유틸
// =====================
const isAndroidWebView = () => {
  try {
    return typeof window !== "undefined" && !!window.Android;
  } catch {
    return false;
  }
};

const androidCall = (fn, ...args) => {
  try {
    const a = window.Android;
    if (!a || typeof a[fn] !== "function") return false;
    a[fn](...args);
    return true;
  } catch {
    return false;
  }
};

// =====================
// ✅ STT 후처리 유틸 (모듈 스코프 — 웹/안드로이드 공통 사용)
//    글자 반복 방지 + MES 용어 오인식 보정
// =====================

// 1) 연속 중복 어절 제거: "재고 재고 조회 조회해줘" -> "재고 조회해줘"
const dedupeAdjacentWords = (text) => {
  const words = String(text || "")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean);
  const out = [];
  for (const w of words) {
    const prev = out[out.length - 1];
    if (!prev) {
      out.push(w);
      continue;
    }
    if (prev === w) continue; // 완전 중복
    if (w.startsWith(prev) && w.length - prev.length <= 4) {
      out[out.length - 1] = w; // 부분확정 -> 최종확정
      continue;
    }
    if (prev.startsWith(w) && prev.length - w.length <= 4) continue;
    out.push(w);
  }
  return out.join(" ");
};

// 2) 반복되는 "구절(n-gram)" 제거 — 안드로이드 누적 STT의 핵심 방어
//    예: "7월 달 거 7월 달 거 7월 달 거 평균으로" -> "7월 달 거 평균으로"
//    길이 1~8 어절의 연속 반복 구절을 접어준다.
//    ※ STT가 같은 말을 공백만 다르게 인식("평균으로" vs "평 균으로")하는 경우가 많아
//      구절 비교 시 공백을 무시하고 대조한다.
const _noSpace = (s) => s.replace(/\s+/g, "");
const collapseRepeatedPhrases = (text) => {
  let words = String(text || "")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean);
  if (words.length < 2) return words.join(" ");

  for (let n = Math.min(8, Math.floor(words.length / 2)); n >= 1; n -= 1) {
    let changed = true;
    while (changed) {
      changed = false;
      const res = [];
      let i = 0;
      while (i < words.length) {
        if (i + 2 * n <= words.length) {
          const a = _noSpace(words.slice(i, i + n).join(" "));
          const b = _noSpace(words.slice(i + n, i + 2 * n).join(" "));
          if (a && a === b) {
            let j = i + n;
            while (
              j + n <= words.length &&
              _noSpace(words.slice(j, j + n).join(" ")) === a
            ) {
              j += n;
            }
            for (let k = 0; k < n; k += 1) res.push(words[i + k]);
            i = j;
            changed = true;
            continue;
          }
        }
        res.push(words[i]);
        i += 1;
      }
      words = res;
    }
  }
  return words.join(" ");
};

// 3) 통짜 반복 제거: "재고조회재고조회" / "재고조회 재고조회"
const dedupeWholeRepeat = (text) => {
  let s = String(text || "").trim();
  if (!s) return s;
  const half = Math.floor(s.length / 2);
  const a = s.slice(0, half).trim();
  const b = s.slice(s.length - half).trim();
  if (a && a === b) return a;
  const parts = s.split(" ");
  if (parts.length % 2 === 0) {
    const h = parts.length / 2;
    const left = parts.slice(0, h).join(" ");
    const right = parts.slice(h).join(" ");
    if (left && left === right) return left;
  }
  return s;
};

// 4) MES 용어 오인식 보정
const MES_STT_CORRECTIONS = [
  [/가동\s*룰/g, "가동률"],
  [/가동\s*율/g, "가동률"],
  [/가동\s*늘/g, "가동률"],
  [/수저(?=\s|를|을|$)/g, "수주"],
  [/발쭈|발주서/g, "발주"],
  [/입꼬|입고서/g, "입고"],
  [/출하서|추하/g, "출하"],
  [/케이\s*피\s*아이|케피아이/g, "KPI"],
  // KPI를 STT가 자주 흘리는 형태들 (kti, kpi 소문자, 케이티아이 등)
  [/\bkti\b/gi, "KPI"],
  [/\bkpi\b/g, "KPI"],
  [/케이티아이|케이 티 아이/g, "KPI"],
  [/로트|랏트|랏/g, "LOT"],
  [/비오엠|비 오 엠/g, "BOM"],
  [/제고(?=\s|를|을|현황|$)/g, "재고"],
  [/설비\s*가동\s*늘/g, "설비 가동률"],
  // --- 경상도 억양 등으로 자주 나는 오인식 (조회 맥락일 때만 치환) ---
  // "입구는/입구 어때/입구 조회" -> 입고  (단독 '입구'는 안 건드림)
  [/입구(?=\s*(는|은|이|가|를|좀|현황|목록|내역|조회|확인|어때|어떄|보여|알려))/g, "입고"],
  [/출구(?=\s*(는|은|이|가|를|좀|현황|목록|내역|조회|확인|어때|어떄|보여|알려))/g, "출고"],
  [/수주는|수주은/g, "수주 "],
  [/발주는|발주은/g, "발주 "],
  // "품묵/품먹" 류 -> 품목
  [/품묵|품먹/g, "품목"],
  // "재공품/재공/잰고" 억양 흔들림 -> 재고 (조회 맥락)
  [/잰고|잭고/g, "재고"],

  // =====================================================================
  // ✅ ai_chat_trace 실측 오인식 보정 (2026-06 ~ 2026-10 로그 기반)
  //    음성 입력이라 MES 도메인어가 일상어로 들어오는 케이스.
  //    오탐을 막기 위해 '조회 문맥'이 뒤따를 때만 치환한다(lookahead).
  //    ⚠️ lookbehind는 구형 iOS Safari에서 깨지므로 사용하지 않는다.
  // =====================================================================
  // "10월" -> "시골"   (예: "시골 전체로 확인해 줘")
  [/시골(?=\s*(전체|달|월|기준|거|걸로|데이터|로))/g, "10월"],
  // "최근" -> "퇴근"   (예: "퇴근 입고 확인해 줘")
  [/퇴근(?=\s*(입고|출고|출하|수주|발주|생산|재고|품목|내역|목록|조회|확인|데이터|실적))/g, "최근"],
  // "생산" -> "신상"   (예: "신상 조회하는 화면은 어디")
  [/신상(?=\s*(조회|화면|내역|목록|데이터|실적|수량|작업|량|확인))/g, "생산"],
  // "화면" -> "가면/라면"  (예: "등록하는 라면", "조회하는 가면은 어디")
  [/(조회|등록|수정|확인|입력|저장|작성)(하는|할|한)?\s*(가면은|라면은|가면|라면)/g, "$1$2 화면"],
  // "총액" -> "총회"   (예: "볼 수주총회")
  [/수주\s*총회/g, "수주 총액"],
  [/발주\s*총회/g, "발주 총액"],
  // "N월달" -> "N월단" (예: "3.25t는 9월단 생산갯수")
  [/(\d{1,2})\s*월\s*단(?=\s|$|의|에|로|걸|거)/g, "$1월 달"],
  // "수주" -> "수지"   (예: "수지 데이터 알려 줘")
  [/수지(?=\s*(데이터|정보|내역|목록|총액|금액|조회|확인|건수|현황))/g, "수주"],
  // "왜" -> "외"       (예: "외 미연결이야")
  [/(^|\s)외\s+(?=미연결|연결|안\s*되|안됨|이렇게|이리)/g, "$1왜 "],
  // "입고" -> "치고"   (예: "치고 들어온 거 있어")
  [/치고(?=\s*(들어온|들어왔|된|한|내역|확인))/g, "입고"],
];

const applyMesCorrections = (text) => {
  let s = String(text || "");
  for (const [re, to] of MES_STT_CORRECTIONS) s = s.replace(re, to);
  return s;
};

// 2.5) 점진적 누적(prefix 반복) 대응
//   안드로이드 STT가 partial을 매번 통째로 다시 보내면
//   "A / A B / A B C ..." 처럼 같은 시작 구절이 여러 번 반복되며 길어진다.
//   → 시작 2~4어절이 3회 이상 반복되면, "마지막 반복 시작점부터"가 최종 발화일 확률이 높다.
const collapseIncrementalPrefix = (text) => {
  const words = String(text || "")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean);
  if (words.length < 6) return words.join(" ");

  for (let n = 2; n <= 4; n += 1) {
    if (words.length < n * 3) continue;
    const head = _noSpace(words.slice(0, n).join(" "));
    if (!head) continue;

    const starts = [];
    for (let i = 0; i + n <= words.length; i += 1) {
      if (_noSpace(words.slice(i, i + n).join(" ")) === head) starts.push(i);
    }
    // 3회 이상 반복되면 마지막 시작점부터를 최종 발화로 간주
    if (starts.length >= 3) {
      const lastStart = starts[starts.length - 1];
      return words.slice(lastStart).join(" ");
    }
  }
  return words.join(" ");
};

// 최종 정리: 통짜반복 -> 구절반복 -> 점진누적 -> 어절중복 -> 용어보정
const cleanSttText = (text) => {
  let s = dedupeWholeRepeat(text);
  s = collapseRepeatedPhrases(s);
  s = collapseIncrementalPrefix(s);
  s = collapseRepeatedPhrases(s);
  s = dedupeAdjacentWords(s);
  s = applyMesCorrections(s);
  return s.replace(/\s+/g, " ").trim();
};

// =====================
// ✅ Web TTS 지원 체크
// =====================
const hasWebTTS =
  typeof window !== "undefined" &&
  typeof window.speechSynthesis !== "undefined" &&
  typeof window.SpeechSynthesisUtterance !== "undefined";

// =====================
// ✅ Reverse Geocode (시/군까지만 + 상세주소)
// - 무료 OSM Nominatim 사용
// - 상용 대량 트래픽이면 카카오/네이버로 교체 권장
// =====================

const pickCityOrCounty = (addr = {}) => {
  return (
    addr.city ||
    addr.municipality ||
    addr.city_district ||
    addr.borough ||
    addr.town ||
    addr.village ||
    addr.suburb ||
    addr.county ||
    addr.state ||
    ""
  );
};

const trimToSiGun = (name) => {
  const t = String(name || "").trim();
  if (!t) return "";

  const first = t.split(/\s+/)[0];

  return first;
};


// ✅ 시/군 라벨 축약: 서울특별시→서울시, 부산광역시→부산시, 제주특별자치시→제주시 등
const shortenSiGunLabel = (label) => {
  let t = String(label || "").trim();
  if (!t) return "";
  // 공백 있으면 첫 토큰만
  t = t.split(/\s+/)[0];
  t = t
    .replace(/특별자치시$/g, "시")
    .replace(/특별시$/g, "시")
    .replace(/광역시$/g, "시")
    .replace(/특별자치도$/g, "도");
  return t;
};

const geoLabelBoxStyle = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  padding: "4px 10px",
  border: "none",
  borderRadius: 8,
  backgroundColor: "transparent",
  minWidth: 90,
};
const geoLabelStyle = {
  fontSize: 14,
  color: "#6b7280",
  fontWeight: 700,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  textAlign: "right",
  minWidth: 0,
};
const locationIconStyle = {
  width: 32,
  height: 32,
  padding: "2px",
  border: "none",
  borderRadius: "8px",
  backgroundColor: "#fff",
  color: "#6b7280",
  "&:hover": { backgroundColor: "#f3f4f6" },
};
const closeButtonStyle = {
  width: 34,
  height: 34,
  padding: "4px",
  borderRadius: 2,
  border: "1px solid #e5e7eb",
  backgroundColor: "#fff",
  "&:hover": { backgroundColor: "#f3f4f6" },
};

// ✅ Nominatim 주소를 한국식 순서로 최대한 정리
const formatKoreanAddressFromNominatim = (addr = {}) => {
  const pick = (v) => String(v || "").trim();

  // 시/군(라벨)
  const siGunRaw = trimToSiGun(pickCityOrCounty(addr) || addr.state || "");
  const siGun = shortenSiGunLabel(siGunRaw);

  // 구/군
  const maybeCounty = pick(addr.county);
  const guGun =
    pick(addr.city_district) ||
    pick(addr.borough) ||
    ((/([가-힣]+(구|군))$/.test(maybeCounty)) ? maybeCounty : "");

  // 동/읍/면/리
  const dong =
    pick(addr.suburb) ||
    pick(addr.neighbourhood) ||
    pick(addr.quarter) ||
    pick(addr.town) ||
    pick(addr.village) ||
    "";

  // 도로명 + 번지
  const road = pick(addr.road) || pick(addr.residential) || pick(addr.pedestrian) || "";
  const house = pick(addr.house_number);
  const detail = [road, house].filter(Boolean).join(" ").trim();

  const parts = [siGun, guGun, dong, detail].filter(Boolean);

  // 중복 제거(연속 중복)
  const uniq = [];
  for (const part of parts) {
    if (!uniq.length || uniq[uniq.length - 1] !== part) uniq.push(part);
  }
  return uniq.join(" ").trim();
};

const reverseGeocodeToGeoInfo = async (lat, lng) => {
  try {
    const la = Number(lat);
    const lo = Number(lng);
    if (!Number.isFinite(la) || !Number.isFinite(lo)) return { label: "", full: "" };

    // Nominatim reverse
    const url =
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2` +
      `&lat=${encodeURIComponent(la)}` +
      `&lon=${encodeURIComponent(lo)}` +
      `&accept-language=ko`;

    const res = await fetch(url, {
      method: "GET",
      headers: {},
    });

    if (!res.ok) return { label: "", full: "" };
    const data = await res.json();

    const addr = data?.address || {};
    const rawLabel = trimToSiGun(pickCityOrCounty(addr) || addr.state || "");

    // ✅ 라벨: '서울특별시' → '서울시' 처럼 축약
    const label = shortenSiGunLabel(rawLabel) || "";

    // ✅ 상세주소: 시/군/구/동 + 도로명/번지 순서로 구성
    const full =
      formatKoreanAddressFromNominatim(addr) ||
      String(data?.display_name || "").trim();

    return { label, full };

  } catch (e) {
    return { label: "", full: "" };
  }
};

function ChatModal({
  chatModalClose,
  fullScreen = false,
  showSidebarButton = false,
  onSidebarClick,
}) {
  const appStore = useContext(AppContext);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const handleSendTextRef = useRef(null);

  // ✅ PC 첨부(엑셀 업로드)
  const fileInputRef = useRef(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [attachMenuAnchor, setAttachMenuAnchor] = useState(null);
  const attachMenuOpen = Boolean(attachMenuAnchor);

  // ✅ 드래그앤드랍 업로드
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);

  // ✅ DataGrid 드롭다운을 위한 공통코드 상태 추가
  const [userTypeModalOption, setUserTypeModalOption] = useState([]);
  const [unitCurrencyModalOption, setUnitCurrencyModalOption] = useState([]);
  const [qualityYnModalOption, setQualityYnModalOption] = useState([
    { value: 'Y', label: 'Y' },
    { value: 'N', label: 'N' }
  ]);

  // =====================
  // ✅ 등록 모달 상태
  // =====================
  // 컴포넌트 안쪽 (orderRegOpen 근처)
  const [orderRegOpen, setOrderRegOpen] = useState(false);
  const [orderRegPayload, setOrderRegPayload] = useState(null);
  const [orderMenuUpdateYN, setOrderMenuUpdateYN] = useState('N');
  
  const [purchaseRegOpen, setPurchaseRegOpen] = useState(false);
  const [purchaseRegPayload, setPurchaseRegPayload] = useState(null);
  const [purchaseMenuUpdateYN, setPurchaseMenuUpdateYN] = useState('N');

  const [incomingRegOpen, setIncomingRegOpen] = useState(false);
  const [incomingRegPayload, setIncomingRegPayload] = useState(null);
  const [incomingMenuUpdateYN, setIncomingMenuUpdateYN] = useState('N');

  const [outgoingRegOpen, setOutgoingRegOpen] = useState(false);
  const [outgoingRegPayload, setOutgoingRegPayload] = useState(null);
  const [outgoingMenuUpdateYN, setOutgoingMenuUpdateYN] = useState('N');

  // ✅ 챗봇 창 열릴 때 각 화면 권한 체크
  useEffect(() => {
    try {
      const accessList = JSON.parse(localStorage.getItem("nav") || "[]");
      if (!Array.isArray(accessList)) return;

      // 메뉴 url 앞 3세그먼트(/p/order 등)로 매칭하는 헬퍼
      const findUpdateYn = (path) => {
        const target = String(path).split("/").slice(0, 3).join("/");
        const hit = accessList.find(
          (item) => String(item?.menu_url) === target
        );
        return hit?.menu_update_yn || "N";
      };

      setOrderMenuUpdateYN(findUpdateYn("/p/order"));
      setPurchaseMenuUpdateYN(findUpdateYn("/p/purchase"));
      setIncomingMenuUpdateYN(findUpdateYn("/p/incoming"));
      setOutgoingMenuUpdateYN(findUpdateYn("/p/outgoing"));
    } catch (e) {
      console.error("권한 에러:", e);
    }
  }, []);

  // ✅ 공통코드 조회
  useEffect(() => {
    axios.get('/api/Common/CodeDetailList')
      .then((response) => {
        var user_type_array_modal = [];
        var unit_currency_array_modal = [];
        for(let i=0; i<(response.data).length; i++){
          if((response.data)[i].code_id === '0017'){ // 통화단위
            unit_currency_array_modal.push({ value: (response.data)[i].code_detail_seq, label: (response.data)[i].code_detail_name });
          } else if((response.data)[i].code_id === '0020'){ // 담당자유형
            user_type_array_modal.push({ value: (response.data)[i].code_detail_seq, label: (response.data)[i].code_detail_name });
          }
        }
        setUnitCurrencyModalOption(unit_currency_array_modal);
        setUserTypeModalOption(user_type_array_modal);
      })
      .catch((error) => {
        console.log("공통코드 조회 에러:", error);
      });
  }, []);

  const openOrderReg = (payload = null) => {
    setOrderRegPayload(payload);
    setOrderRegOpen(true);
  };

  // ===== 설정 =====
  const closeOrderReg = () => {
    setOrderRegOpen(false);
    setOrderRegPayload(null);
  };

  // ✅ [추가] 수주 등록 성공 시 호출될 함수
  const handleRegSuccessSave = (selectID) => {
    closeOrderReg(); // 등록 모달 닫기
    
    Swal.fire({
      title: '수주 등록 완료',
      text: '상세 내역을 확인하시겠습니까?',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: '상세',
      cancelButtonText: '닫기'
    }).then((result) => {
      if (result.isConfirmed) {
        // ✅ 새 탭으로 수주 관리 페이지 열기 (파라미터로 selectID 전달)
        window.open(`/p/order/${selectID}`, '_blank');
      }
    });
  };

  const openPurchaseReg = (payload = null) => {
    setPurchaseRegPayload(payload);
    setPurchaseRegOpen(true);
  };
  const closePurchaseReg = () => {
    setPurchaseRegOpen(false);
    setPurchaseRegPayload(null);
  };
  const handlePurchaseRegSuccessSave = (selectID) => {
    closePurchaseReg();
    Swal.fire({
      title: '발주 등록 완료',
      text: '상세 내역을 확인하시겠습니까?',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: '상세',
      cancelButtonText: '닫기'
    }).then((result) => {
      if (result.isConfirmed) {
        window.open(`/p/purchase/${selectID}`, '_blank');
      }
    });
  };

  const openIncomingReg = (payload = null) => {
    setIncomingRegPayload(payload);
    setIncomingRegOpen(true);
  };
  const closeIncomingReg = () => {
    setIncomingRegOpen(false);
    setIncomingRegPayload(null);
  };
  const handleIncomingRegSuccessSave = (selectID) => {
    closeIncomingReg();
    Swal.fire({
      title: '입고 등록 완료',
      text: '상세 내역을 확인하시겠습니까?',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: '상세',
      cancelButtonText: '닫기'
    }).then((result) => {
      if (result.isConfirmed) {
        window.open(`/p/incoming/${selectID}`, '_blank');
      }
    });
  };

  const openOutgoingReg = (payload = null) => {
    setOutgoingRegPayload(payload);
    setOutgoingRegOpen(true);
  };
  const closeOutgoingReg = () => {
    setOutgoingRegOpen(false);
    setOutgoingRegPayload(null);
  };
  const handleOutgoingRegSuccessSave = (selectID) => {
    closeOutgoingReg();
    Swal.fire({
      title: '출하 등록 완료',
      text: '상세 내역을 확인하시겠습니까?',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: '상세',
      cancelButtonText: '닫기'
    }).then((result) => {
      if (result.isConfirmed) {
        window.open(`/p/outgoing/${selectID}`, '_blank');
      }
    });
  };

  // ===== 설정 =====
  const HISTORY_MAX = 20;
  //  실제 한도는 서버가 정한다 (user_info.ai_daily_limit > company_info.ai_daily_limit).
  //  이 값은 서버 응답을 받기 전이나 /quota 조회가 실패했을 때 쓰는 표시용 기본값이다.
  //  예전에는 이 상수가 유일한 제한이었고 localStorage 로만 셌기 때문에
  //  F12 로 지우거나 시크릿창을 쓰면 그냥 뚫렸다.
  const DAILY_LIMIT_FALLBACK = 10;

  // ✅ 렌더 중 반복 호출을 피하기 위해 1회만 읽어두는 회사 정보
  const companyId = useMemo(() => {
    try {
      return Number(localStorage.getItem("company_id") ?? "0");
    } catch (e) {
      return 0;
    }
  }, []);
  const companyName = useMemo(() => {
    try {
      return localStorage.getItem("company_nm") ?? "";
    } catch (e) {
      return "";
    }
  }, []);

  // ✅ 일일 사용횟수 저장/복원 (회사+날짜 단위, 새로고침해도 유지)
  const askCountStorageKey = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    return `mes_chat_ask_${companyId}_${today}`;
  }, [companyId]);

  const readAskCount = () => {
    try {
      const raw = localStorage.getItem(askCountStorageKey);
      const n = parseInt(raw ?? "0", 10);
      return Number.isFinite(n) && n > 0 ? n : 0;
    } catch (e) {
      return 0;
    }
  };

  const writeAskCount = (n) => {
    try {
      localStorage.setItem(askCountStorageKey, String(n));
    } catch (e) {}
  };

  // ✅ 대화 내용 저장/복원 (sessionStorage, 회사 단위)
  const messagesStorageKey = useMemo(
    () => `mes_chat_messages_${companyId}`,
    [companyId]
  );

  const isLocalhost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");
  const API_URL = isLocalhost ? "http://127.0.0.1:8585/chat" : "/chat";
  const QUOTA_URL = isLocalhost ? "http://127.0.0.1:8585/quota" : "/quota";

  // ✅ 신원 토큰: Spring(세션 보유)이 발급 -> 챗봇(FastAPI)이 검증.
  //    /chat 은 Spring 을 안 거치므로 Interceptor 의 세션 검사가 닿지 않는다.
  //    이 토큰이 "누가 물었는지"를 챗봇에 전달하는 유일한 경로다.
  const CHAT_TOKEN_URL = "/api/Common/ChatToken";

  const isMobileChat =
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/m/chat");

  // ✅ 정책(요청): 모바일(/m/chat)에서만 마이크, PC에서는 숨김(=첨부만), 테블릿에서는 마이크 허용.
  const enableMic = isMobileChat || localStorage.getItem("deviceType") === "T";

  // ✅ 모바일(/m/chat)은 사실상 fullScreen처럼 동작해야 안정적
  const shouldFull = fullScreen || isMobileChat;

  // ✅ 상단 헤더 높이(페이지 고정 헤더 포함) 자동 보정
  const [topOffset, setTopOffset] = useState(0);

  // ✅ 상단 제목 오른쪽에 표시할 위치 라벨 (예: 평택시)
  const [geoLabel, setGeoLabel] = useState(() => {
    try {
      if (typeof window === "undefined") return "";
      const key = buildGeoLabelStorageKey();
      return sessionStorage.getItem(key) || "";
    } catch (e) {
      return "";
    }
  });

  // ✅ 롱프레스 툴팁에 표시할 상세주소
  const [geoFull, setGeoFull] = useState(() => {
    try {
      if (typeof window === "undefined") return "";
      const key = buildGeoFullStorageKey();
      return sessionStorage.getItem(key) || "";
    } catch (e) {
      return "";
    }
  });

  // ✅ 주소 툴팁(아이콘 클릭) 5초 후 자동 닫힘
  const [geoTipOpen, setGeoTipOpen] = useState(false);
  const geoTipTimerRef = useRef(null);

  const closeGeoTip = () => {
    setGeoTipOpen(false);
    if (geoTipTimerRef.current) {
      clearTimeout(geoTipTimerRef.current);
      geoTipTimerRef.current = null;
    }
  };

  const openGeoTip = () => {
    if (!geoFull) return;
    setGeoTipOpen(true);
    if (geoTipTimerRef.current) clearTimeout(geoTipTimerRef.current);
    geoTipTimerRef.current = setTimeout(() => {
      setGeoTipOpen(false);
      geoTipTimerRef.current = null;
    }, 5000);
  };
  // =========================================================
  // ✅ (핵심) iOS/Android WebView 뷰포트 높이 변수 + 상단 헤더 높이 감지
  // =========================================================
  useEffect(() => {
    if (typeof window === "undefined") return;

    const setAppHeight = () => {
      const h = window.visualViewport?.height || window.innerHeight || 0;
      document.documentElement.style.setProperty("--app-height", `${h}px`);
    };

    const measureTopHeaders = () => {
      try {
        const selectors = [
          "header",
          "#header",
          "#appHeader",
          ".app-header",
          ".MuiAppBar-root",
          ".navbar",
          ".topbar",
          ".header",
          "[data-fixed-header='true']",
        ];

        const candidates = [];
        selectors.forEach((sel) => {
          document.querySelectorAll(sel).forEach((el) => candidates.push(el));
        });

        const uniq = Array.from(new Set(candidates));
        let sum = 0;

        for (const el of uniq) {
          if (!el) continue;

          const style = window.getComputedStyle(el);
          const pos = style.position;
          if (pos !== "fixed" && pos !== "sticky") continue;

          const rect = el.getBoundingClientRect();
          if (rect.height <= 0) continue;
          if (rect.bottom <= 0) continue;
          if (rect.top > 4) continue;
          if (rect.width < window.innerWidth * 0.6) continue;

          sum += rect.height;
        }

        setTopOffset(Math.round(sum));
      } catch (e) {
        setTopOffset(0);
      }
    };

    const tick = () => {
      setAppHeight();
      measureTopHeaders();
    };

    tick();

    window.addEventListener("resize", tick);
    window.addEventListener("orientationchange", tick);
    window.visualViewport?.addEventListener("resize", tick);
    window.visualViewport?.addEventListener("scroll", tick);

    const t1 = setTimeout(tick, 200);
    const t2 = setTimeout(tick, 800);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener("resize", tick);
      window.removeEventListener("orientationchange", tick);
      window.visualViewport?.removeEventListener("resize", tick);
      window.visualViewport?.removeEventListener("scroll", tick);
    };
  }, []);

  // ✅ shouldFull일 때 바깥(body/html) 스크롤 차단
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!shouldFull) return;

    const html = document.documentElement;
    const body = document.body;

    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyHeight = body.style.height;

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.height = "100%";

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      body.style.height = prevBodyHeight;
    };
  }, [shouldFull]);

  // ✅ messages 초기값: sessionStorage 복원 (없으면 인사말)
  const buildGreeting = () => ({
    role: "assistant",
    content:
      "안녕하세요! " +
      companyName +
      " MES 관련해서 무엇이든 물어보세요 :)",
    ts: Date.now(),
    attachments: [],
  });

  const [messages, setMessages] = useState(() => {
    try {
      if (typeof window !== "undefined") {
        const raw = sessionStorage.getItem(messagesStorageKey);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed) && parsed.length > 0) return parsed;
        }
      }
    } catch (e) {}
    return [buildGreeting()];
  });

  // ✅ messages 변경 시 sessionStorage에 저장 (새로고침 후 복원용)
  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      // 용량 방어: 최근 60개만, 저장에 불필요하게 무거운 필드는 정리
      const MAX_PERSIST = 60;
      const slim = messages.slice(-MAX_PERSIST).map((m) => ({
        role: m.role,
        content: m.content,
        ts: m.ts,
        attachments: Array.isArray(m.attachments) ? m.attachments : [],
      }));
      sessionStorage.setItem(messagesStorageKey, JSON.stringify(slim));
    } catch (e) {
      // sessionStorage 용량 초과 등은 무시(대화는 화면엔 유지됨)
    }
  }, [messages, messagesStorageKey]);

  const [input, setInput] = useState("");
  const inputStateRef = useRef("");
  useEffect(() => {
    inputStateRef.current = input;
  }, [input]);

  const [isLoading, setIsLoading] = useState(false);

  // ✅ 콜백(특히 Android bridge)에서 최신 상태를 보게 하기 위한 Ref
  const isLoadingRef = useRef(false);
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  // ===== 위치 저장 (React에서 쓸 수 있게만) =====
  // geoRef.current = { lat, lng, accuracy, ts, from }
  const geoRef = useRef(null);

  // ===== STT =====
  const [isListening, setIsListening] = useState(false);
  const isListeningRef = useRef(false);
  useEffect(() => {
    isListeningRef.current = isListening;
  }, [isListening]);
  const [sttDraft, setSttDraft] = useState(""); // 웹 STT interim 표시용(기존 UI 유지)
  const recognitionRef = useRef(null);
  const sttFinalRef = useRef("");
  const sttTimerRef = useRef(null);

  const sttDraftRef = useRef("");
  useEffect(() => {
    sttDraftRef.current = sttDraft;
  }, [sttDraft]);

  const sttSilenceRef = useRef(null);
  const sttAutoSendRef = useRef(false);

  const STT_SILENCE_MS = 1800;

  const sttHasSpeechRef = useRef(false);
  const sttBaseInputRef = useRef("");

  const sttUsedRef = useRef(false);
  const lastSendByMicRef = useRef(false);

  // ✅ “한 번 시작(버튼 클릭)” 이후, 입력이 비어있으면 자동 대기 유지
  const autoListenRef = useRef(false);

  // ✅ stop 후 전송 딜레이
  const STT_SEND_DELAY_MS = 1000;

  // ✅ "입력폼 반영 후 변경 없으면 자동전송"용 디바운스
  const pendingAutoSendTimerRef = useRef(null);
  const pendingAutoSendTextRef = useRef("");
  const cancelPendingAutoSend = () => {
    if (pendingAutoSendTimerRef.current) {
      clearTimeout(pendingAutoSendTimerRef.current);
      pendingAutoSendTimerRef.current = null;
    }
    pendingAutoSendTextRef.current = "";
  };
  useEffect(() => {
    return () => cancelPendingAutoSend();
  }, []);

  // ===== TTS =====
  const [isSpeaking, setIsSpeaking] = useState(false);
  const isSpeakingRef = useRef(false);
  useEffect(() => {
    isSpeakingRef.current = isSpeaking;
  }, [isSpeaking]);
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const utterRef = useRef(null);

  const ttsQueueRef = useRef([]);
  const ttsQueueIdxRef = useRef(0);

  const [stickyLeft, setStickyLeft] = useState(0);

  // ✅ 서버가 알려준 한도. -1 이면 무제한.
  const [dailyLimit, setDailyLimit] = useState(DAILY_LIMIT_FALLBACK);
  const [quotaUnlimited, setQuotaUnlimited] = useState(false);

  // ✅ 신원 토큰 (Spring 세션 -> 서명 토큰)
  const chatTokenRef = useRef("");

  // ✅ 오늘 사용횟수를 저장소에서 복원(새로고침해도 유지).
  //    표시 전용이다. 실제 차단은 서버가 한다.
  const [askCount, setAskCount] = useState(() => readAskCount());
  const [locked, setLocked] = useState(
    () => readAskCount() >= DAILY_LIMIT_FALLBACK
  );
  const lockedRef = useRef(false);
  useEffect(() => {
    lockedRef.current = locked;
  }, [locked]);

  // ✅ 신원 토큰 + 서버 사용량을 받아온다.
  //
  //    순서가 중요하다: 토큰을 먼저 받아야 /quota 가 누구인지 알 수 있다.
  //    토큰 발급은 Spring 세션으로 보호되므로(Interceptor), 로그인하지 않았으면
  //    여기서 실패한다. 그때는 로컬 폴백 표시로 두고 전송 시 서버가 거부한다.
  useEffect(() => {
    let alive = true;

    const bootstrap = async () => {
      // 1) 토큰
      try {
        const r = await axios.get(CHAT_TOKEN_URL, {
          withCredentials: true,
          timeout: 10000,
        });
        if (!alive) return;
        if (r.data?.ok && r.data?.token) {
          chatTokenRef.current = r.data.token;
        } else {
          // reason: no_session / identity_not_in_session / misconfigured
          console.warn("[CHAT] 토큰 발급 실패:", r.data?.reason);
        }
      } catch (e) {
        if (!alive) return;
        console.warn("[CHAT] 토큰 발급 요청 실패:", e?.message || e);
      }

      // 1-5) 카카오톡 연결 마무리
      //      카카오 링크를 눌렀다가 로그인으로 튕긴 경우, 로그인 후
      //      /m/chat?kkolink=<코드> 로 돌아온다. 토큰이 있으니 여기서 바로 연결한다.
      //      (MES Login.jsx 는 /m,/t,/p 로 시작하는 경로로만 복귀시키므로
      //       서버 주소로 직접 돌아올 수가 없어서 이 방식을 쓴다)
      try {
        var kkoCode = new URLSearchParams(window.location.search).get("kkolink");
      } catch (e) {
        kkoCode = null;
      }
      if (kkoCode && chatTokenRef.current) {
        try {
          const lr = await axios.post(
            "/api/kakao/link/confirm",
            { code: kkoCode },
            { timeout: 10000,
              headers: { "X-DIFF-CHAT-TOKEN": chatTokenRef.current } }
          );
          if (!alive) return;
          const okMsg = lr.data?.ok
            ? `카카오톡 연결이 완료되었습니다.${lr.data?.user_name ? " (" + lr.data.user_name + " 님)" : ""}\n이제 카카오톡에서도 질문하실 수 있습니다.`
            : (lr.data?.message || "카카오톡 연결에 실패했습니다.");
          setMessages((prev) => [
            ...prev,
            { role: "assistant", content: okMsg, ts: Date.now(), attachments: [] },
          ]);
        } catch (e) {
          if (!alive) return;
          setMessages((prev) => [
            ...prev,
            { role: "assistant",
              content: "카카오톡 연결 중 오류가 발생했습니다. 카카오톡에서 다시 시도해 주세요.",
              ts: Date.now(), attachments: [] },
          ]);
        }
        // 주소에서 kkolink 를 지운다 — 새로고침할 때 다시 연결 시도하지 않도록.
        try {
          const u = new URL(window.location.href);
          u.searchParams.delete("kkolink");
          window.history.replaceState({}, "", u.pathname + u.search + u.hash);
        } catch (e) {}
      }

      // 2) 사용량 (토큰이 없으면 서버가 누구인지 모르므로 건너뛴다)
      if (!chatTokenRef.current) return;
      try {
        const q = await axios.get(QUOTA_URL, {
          timeout: 10000,
          headers: { "X-DIFF-CHAT-TOKEN": chatTokenRef.current },
        });
        if (!alive) return;
        if (q.data?.ok) {
          const lim = Number(q.data.limit);
          const used = Number(q.data.used);
          if (Number.isFinite(lim)) {
            setDailyLimit(lim);
            setQuotaUnlimited(lim < 0);
          }
          if (Number.isFinite(used)) {
            setAskCount(used);
            writeAskCount(used);
          }
          if (q.data.allowed === false) setLocked(true);
        }
      } catch (e) {
        if (!alive) return;
        console.warn("[CHAT] 사용량 조회 실패:", e?.message || e);
      }
    };

    bootstrap();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ askCount 변경 시 저장소에 반영
  useEffect(() => {
    writeAskCount(askCount);
    if (!quotaUnlimited && dailyLimit > 0 && askCount >= dailyLimit) setLocked(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askCount]);

  useEffect(() => {
    if (!sttUsedRef.current) return;
    cancelPendingAutoSend();
    const candidate = input.trim();
    if (!candidate) return;
    if (locked || isLoading || isListening) return;
    pendingAutoSendTextRef.current = candidate;
    pendingAutoSendTimerRef.current = setTimeout(() => {
      pendingAutoSendTimerRef.current = null;
      const pendingText = pendingAutoSendTextRef.current;
      pendingAutoSendTextRef.current = "";
      if (!pendingText) return;
      handleSendTextRef.current?.(pendingText, false);
    }, 1000);
  }, [input, locked, isLoading, isListening]);

  const [lastSqlKey, setLastSqlKey] = useState(null);
  const [lastToolParams, setLastToolParams] = useState({});
  const [lastToolRowsPreview, setLastToolRowsPreview] = useState([]);

  useEffect(() => {
    setAskCount(0);
    setLocked(false);
  }, []);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, isLoading]);

  useEffect(() => {
    const t = setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
    return () => clearTimeout(t);
  }, []);

  // ✅ 웹 위치 1회 저장
  const requestWebGeoOnce = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        geoRef.current = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          ts: Date.now(),
          from: "web",
        };

        // ✅ 시/군 라벨 + 상세주소 업데이트
        const info = await reverseGeocodeToGeoInfo(
          geoRef.current.lat,
          geoRef.current.lng
        );
        if (info?.label) {
          setGeoLabel(info.label);
          try {
            const key = buildGeoLabelStorageKey();
            sessionStorage.setItem(key, info.label);
          } catch (e) {}
        }

        if (info?.full) {
          setGeoFull(info.full);
          try {
            const key = buildGeoFullStorageKey();
            sessionStorage.setItem(key, info.full);
          } catch (e) {}
        }
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 12000 }
    );
  };

  // ✅ Android 위치 1회 저장
  const requestAndroidGeoOnce = () => {
    const rid = `geo_${Date.now()}`;
    const ok = androidCall("getLocationOnce", rid);
    if (!ok) requestWebGeoOnce();
  };

  // ✅ 위치 1회 요청(자동)
  const requestGeoOnce = () => {
    if (isAndroidWebView()) requestAndroidGeoOnce();
    else requestWebGeoOnce();
  };

  // ✅ 최초 1회 위치 요청 (이미 캐시가 있으면 굳이 또 안함)
  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      const kLabel = buildGeoLabelStorageKey();
      const kFull = buildGeoFullStorageKey();
      const cachedLabel = sessionStorage.getItem(kLabel) || "";
      const cachedFull = sessionStorage.getItem(kFull) || "";
      if (cachedLabel) setGeoLabel(cachedLabel);
      if (cachedFull) setGeoFull(cachedFull);
      if (cachedLabel) return; // 라벨이 있으면 굳이 다시 조회하지 않음
    } catch (e) {}

    // 너무 즉시 요청하면 UX가 싫을 수 있어 살짝 딜레이
    const t = setTimeout(() => {
      requestGeoOnce();
    }, 600);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const pollId = setInterval(() => {
      requestGeoOnce();
    }, 60000);
    return () => clearInterval(pollId);
  }, []);

  // ✅ Android 브릿지 콜백 바인딩 (STT/TTS/위치)
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isAndroidWebView()) return;

    window.__DIFF_CHAT_STT_RESULT__ = (text) => {
      // ✅ 안드로이드 네이티브 STT도 누적 반복이 올 수 있어 웹과 동일하게 정리
      const t = cleanSttText(String(text || ""));
      setIsListening(false);
      if (!t) return;

      sttUsedRef.current = true;
      lastSendByMicRef.current = true;
      setInput(t);

      // 디바운스(변경 없으면 자동 전송)
      pendingAutoSendTextRef.current = t;
      if (pendingAutoSendTimerRef.current) {
        clearTimeout(pendingAutoSendTimerRef.current);
        pendingAutoSendTimerRef.current = null;
      }

      pendingAutoSendTimerRef.current = setTimeout(() => {
        const cur = String(inputStateRef.current || "").trim();
        const target = String(pendingAutoSendTextRef.current || "").trim();
        if (cur && target && cur === target && !isLoadingRef.current && !lockedRef.current) {
          handleSendTextRef.current?.(cur, true);
        }
      }, STT_SEND_DELAY_MS);
    };

    window.__DIFF_CHAT_STT_ERROR__ = () => {
      setIsListening(false);
    };

    window.__DIFF_CHAT_TTS_START__ = () => {
      setIsSpeaking(true);
      isSpeakingRef.current = true;
      if (isListeningRef.current) {
        try {
          androidCall("stopSTT");
        } catch (e) {}
        setIsListening(false);
      }
    };

    window.__DIFF_CHAT_TTS_END__ = () => {
      setIsSpeaking(false);
      isSpeakingRef.current = false;
      setSpeakingIdx(null);
    };

    window.__DIFF_CHAT_GEO_RESULT__ = async (obj) => {
      try {
        geoRef.current = {
          lat: Number(obj?.lat),
          lng: Number(obj?.lng),
          accuracy: Number(obj?.accuracy),
          ts: Number(obj?.ts) || Date.now(),
          from: "android",
        };

        const info = await reverseGeocodeToGeoInfo(geoRef.current.lat, geoRef.current.lng);
        if (info?.label) {
          setGeoLabel(info.label);
          try {
            const key = buildGeoLabelStorageKey();
            sessionStorage.setItem(key, info.label);
          } catch (e) {}
        }

        if (info?.full) {
          setGeoFull(info.full);
          try {
            const key = buildGeoFullStorageKey();
            sessionStorage.setItem(key, info.full);
          } catch (e) {}
        }
      } catch (e) {}
    };

    return () => {
      try {
        delete window.__DIFF_CHAT_STT_RESULT__;
        delete window.__DIFF_CHAT_STT_ERROR__;
        delete window.__DIFF_CHAT_TTS_START__;
        delete window.__DIFF_CHAT_TTS_END__;
        delete window.__DIFF_CHAT_GEO_RESULT__;
      } catch (e) {}
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.speechSynthesis) return;

    let unlocked = false;

    const unlock = () => {
      if (unlocked) return;
      unlocked = true;
      try {
        window.speechSynthesis.resume();
        const u = new window.SpeechSynthesisUtterance(" ");
        u.volume = 0;
        window.speechSynthesis.speak(u);
        window.speechSynthesis.cancel();
      } catch (e) {}
      window.removeEventListener("touchstart", unlock);
      window.removeEventListener("mousedown", unlock);
    };

    window.addEventListener("touchstart", unlock, { passive: true });
    window.addEventListener("mousedown", unlock);

    return () => {
      window.removeEventListener("touchstart", unlock);
      window.removeEventListener("mousedown", unlock);
    };
  }, []);

  const fmtKoreanDate = (y, m, d) => {
    const mm = String(m || "").replace(/^0/, "") || "0";
    const dd = String(d || "").replace(/^0/, "") || "0";
    return `${y}년 ${mm}월 ${dd}일`;
  };

  const digitsToKorean = (s) => {
    const map = {
      "0": "공",
      "1": "일",
      "2": "이",
      "3": "삼",
      "4": "사",
      "5": "오",
      "6": "육",
      "7": "칠",
      "8": "팔",
      "9": "구",
    };
    return String(s || "")
      .split("")
      .map((ch) => (map[ch] ? map[ch] : ch))
      .join("");
  };

  const cleanupNumberString = (value) =>
    String(value || "")
      .replace(/,/g, "")
      .trim();

  const numberToKoreanWords = (value) => {
    const integerPart = cleanupNumberString(String(value)).split(".")[0];
    const num = parseInt(integerPart, 10);
    if (Number.isNaN(num)) return digitsToKorean(value);
    if (num === 0) return "영";

    const sectionUnits = ["", "만", "억", "조", "경"];

    const readSection = (section) => {
      const digits = ["", "십", "백", "천"];
      const str = String(section);
      let sectionResult = "";
      for (let i = 0; i < str.length; i += 1) {
        const digit = Number(str[str.length - 1 - i]);
        if (!digit) continue;
        const unit = digits[i];
        if (i === 0 || digit !== 1 || !unit) {
          sectionResult = digitsToKorean(digit) + unit + sectionResult;
        } else {
          sectionResult = unit + sectionResult;
        }
      }
      return sectionResult;
    };

    let remaining = num;
    const parts = [];
    let sectionIndex = 0;
    while (remaining > 0) {
      const section = remaining % 10000;
      remaining = Math.floor(remaining / 10000);
      if (section) {
        const sectionText = readSection(section);
        parts.unshift(`${sectionText}${sectionUnits[sectionIndex] || ""}`);
      }
      sectionIndex += 1;
    }

    let result = parts.join("");
    if (!result) result = "영";

    const decimal = String(cleanupNumberString(value)).split(".")[1];
    if (decimal) {
      const decimalText = decimal
        .split("")
        .map((ch) => digitsToKorean(ch))
        .join("");
      result += `점${decimalText}`;
    }

    return result;
  };

  const spellNumberKorean = (value) => numberToKoreanWords(value);

  const spellNativeCounter = (value) => {
    const cleaned = cleanupNumberString(value);
    const num = parseInt(cleaned, 10);
    if (Number.isNaN(num)) return numberToKoreanWords(value);

    const singles = {
      0: "영",
      1: "한",
      2: "두",
      3: "세",
      4: "네",
      5: "다섯",
      6: "여섯",
      7: "일곱",
      8: "여덟",
      9: "아홉",
    };

    const specialTens = {
      10: "열",
      20: "스무",
      30: "서른",
      40: "마흔",
      50: "쉰",
      60: "예순",
      70: "일흔",
      80: "여든",
      90: "아흔",
    };

    if (specialTens[num]) return specialTens[num];
    if (num < 10) return singles[num] || numberToKoreanWords(num);
    if (num < 20) {
      const remainder = num - 10;
      return `열${singles[remainder] || numberToKoreanWords(remainder)}`;
    }

    if (num < 100) {
      const tens = Math.floor(num / 10) * 10;
      const ones = num % 10;
      const tensText = specialTens[tens] || `${numberToKoreanWords(tens)}`;
      if (!ones) return tensText;
      return `${tensText}${singles[ones] || numberToKoreanWords(ones)}`;
    }

    return numberToKoreanWords(num);
  };

  const highlightSpecMultipliers = (text) => {
    return text.replace(/(\d+(?:\.\d+)?)(?:\s*[x×]\s*(\d+(?:\.\d+)?))+/gi, (match) => {
      const pieces = match
        .split(/[x×]/gi)
        .map((piece) => piece.trim())
        .filter(Boolean);
      if (!pieces.length) return match;
      return pieces
        .map((piece, idx) => `${idx > 0 ? " 곱하기 " : ""}${spellNumberKorean(
          piece
        )}`)
        .join("");
    });
  };

  const highlightUnitNumbers = (text) => {
    const units = [
      "호기",
      "개",
      "EA",
      "대",
      "kg",
      "킬로",
      "톤",
      "t",
      "ton",
      "원",
      "달러",
      "\\$",
      "USD",
      "KRW",
      "건",
    ];
    const numberPattern = "\\d{1,3}(?:,\\d{3})*(?:\\.\\d+)?";
    const regex = new RegExp(
      `(${numberPattern})\\s*(${units.join("|")})`,
      "gi"
    );
    return text.replace(regex, (_, numberPart, unitPart) => {
      const cleanedUnit = unitPart.replace(/[^가-힣]/g, "");
      const ordinalUnits = new Set(["건"]);
      const nativeCounterUnits = new Set(["개", "대", "명"]);
      const cleanedNumber = cleanupNumberString(numberPart);
      let spelled;
      if (ordinalUnits.has(cleanedUnit)) {
        spelled = spellNativeCounter(cleanedNumber);
      } else if (nativeCounterUnits.has(cleanedUnit)) {
        spelled = spellNativeCounter(cleanedNumber);
      } else {
        spelled = numberToKoreanWords(cleanedNumber);
      }
      return `${spelled} ${unitPart}`;
    });
  };

  const normalizeSemantic = (s) => {
    let t = String(s ?? "");
    t = t.replace(
      /\bAPI\b(?=.*(지표|성과|현황|달성|목표|분석|관리|기준))/g,
      "KPI"
    );
    t = t.replace(/\bAPI\b/g, "KPI");
    
    t = t
      .replace(/\bCNC\b/gi, "씨앤씨")
      .replace(/\bMCT\b/gi, "엠씨티")
      .replace(/\bURL\b/gi, "유알엘")
      .replace(/\bKPI\b/gi, "케이피아이")
      .replace(/\bEA\b/g, "개")
      .replace(/소주/g, "수주")
      .replace(/우주/g, "수주")
      .replace(/우유/g, "수주")
      .replace(/EA/g, "개");

    t = t.replace(/^\s+$/gm, "");
    t = t.replace(/\n{3,}/g, "\n\n");
    t = t.replace(/\s+$/gm, "");

    return t;
  };

  const ttsFileName = (name) => {
    let s = String(name || "");
    s = s.replace(/[_\-]+/g, " ");
    s = s.replace(/\bDIFF\b/g, "디프");
    s = s.replace(/\.xlsx$/i, " 엑셀 파일");
    s = s.replace(/\.xls$/i, " 엑셀 파일");
    s = s.replace(/\.pdf$/i, " 피디에프 파일");
    s = s.replace(/\.png$/i, " 이미지 파일");
    s = s.replace(/\.jpg$/i, " 이미지 파일");
    s = s.replace(/\.jpeg$/i, " 이미지 파일");
    s = s.replace(/\.csv$/i, " 씨에스브이 파일");
    s = s.replace(/\.+/g, " ");
    s = s.replace(/\s+/g, " ").trim();
    return s;
  };

  const ttsFileSize = (sizeDp) => {
    const t = String(sizeDp || "").trim();
    if (!t) return "";
    return t
      .replace(/KB/i, "킬로바이트")
      .replace(/MB/i, "메가바이트")
      .replace(/GB/i, "기가바이트");
  };

  const ttsNormalize = (s) => {
    let t = normalizeSemantic(s);

    t = t
      .replace(/\bMES\b/g, "엠이에스")
      .replace(/\bCNC\b/g, "씨앤씨")
      .replace(/\bMCT\b/g, "엠씨티")
      .replace(/\bERP\b/g, "이알피")
      .replace(/\bKPI\b/g, "케이피아이")
      .replace(/\bAPI\b/g, "에이피아이");

    t = t.replace(/%/g, "퍼센트");
    t = t.replace(/\$/g, "달러");
    t = t.replace(/₩/g, "원");         
    t = t.replace(/℃/g, "도씨");        
    t = t.replace(/°C/gi, "도씨");      
    t = t.replace(/°/g, "도");          
    t = t.replace(/@/g, "골뱅이");       
    t = t.replace(/\+/g, "플러스");      
    t = t.replace(/=/g, "는");          
    t = t.replace(/&/g, "앤드");        
    t = t.replace(/#/g, "샵");          
    t = t.replace(/~/g, "에서");        

    t = t.replace(/(\d+)\s*EA\b/g, (_, n) => `${n}개`);
    t = t.replace(/\bEA\b/g, "개");

    t = t.replace(
      /(\d{4})-(\d{2})-(\d{2})\s*~\s*(\d{4})-(\d{2})-(\d{2})/g,
      (_, y1, m1, d1, y2, m2, d2) =>
        `${fmtKoreanDate(y1, m1, d1)} 에서 ${fmtKoreanDate(y2, m2, d2)}`
    );
    t = t.replace(/(\d{4})-(\d{2})-(\d{2})/g, (_, y, m, d) =>
      fmtKoreanDate(y, m, d)
    );

    t = t.replace(/^\s*-\s*파일\s*$/gim, "");
    t = t.replace(
      /^\s*-\s*([^\n]+?)\s+(\d+(?:\.\d+)?\s*(?:KB|MB|GB))\s*$/gim,
      (_, fname, fsize) =>
        `파일: ${ttsFileName(fname)}. 용량: ${ttsFileSize(fsize)}.`
    );

    t = t.replace(/\[([^\]]+)\]/g, "$1");
    t = t.replace(/\*/g, "");
    t = t.replace(/^\s*-\s*파일\s*$/gim, ""); 
    // ✅ 콜론 뒤에 줄바꿈을 넣으면 "불량률감소:" 하고 뚝 끊겨 어색하게 읽힘.
    //    콜론은 짧은 쉼(공백)으로 바꿔 자연스럽게 이어 읽도록 한다.
    t = t.replace(/\s*:(?!\/\/)\s*/g, " ");
    // ✅ 줄 앞 머리기호(-, •) 제거: "하이픈 자동검사장비"처럼 읽히는 것 방지
    t = t.replace(/^[ \t]*[-•]\s+/gm, "");
    
    t = highlightSpecMultipliers(t);
    t = highlightUnitNumbers(t);

    t = t.replace(/(\d),(?=\d{3}\b)/g, "$1"); 

    const numberWithCommas = /(?<!\d)(\d{1,3}(?:,\d{3})*)(?:\.(\d+))?(?!\d)/g;
    t = t.replace(numberWithCommas, (matches) =>
      numberToKoreanWords(matches)
    );

    t = t.replace(
      /\b([A-Z])(\d{6,})(?:-(\d{1,}))?\b/g,
      (_, pfx, main, lot) => {
        const key = String(pfx || "").toUpperCase();
        const info = CODE_PREFIX[key];
        if (!info) return `${key}${main}${lot ? "-" + lot : ""}`;

        const mainSpeak = digitsToKorean(main);
        if (lot) {
          const lotSpeak = digitsToKorean(lot);
          return `${info.label} ${mainSpeak} 다시 ${lotSpeak}`;
        }
        return `${info.label} ${mainSpeak}`;
      }
    );

    t = t.replace(/[^a-zA-Z0-9ㄱ-ㅎ가-힣\s.,!?]/g, " ");

    t = t.replace(/^\s+$/gm, "");      
    t = t.replace(/\n{3,}/g, "\n\n");  
    t = t.replace(/\s+$/gm, "");       
    t = t.replace(/ +/g, " ");         

    return t;
  };

  const hasTTS = hasWebTTS;

  const getVoicesSafe = () => {
    if (!hasTTS) return [];
    try {
      return window.speechSynthesis.getVoices() || [];
    } catch {
      return [];
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.speechSynthesis) return;

    const warm = () => {
      try {
        window.speechSynthesis.getVoices();
      } catch (e) {}
    };

    warm();

    const onChanged = () => warm();
    window.speechSynthesis.addEventListener?.("voiceschanged", onChanged);

    return () => {
      window.speechSynthesis.removeEventListener?.("voiceschanged", onChanged);
    };
  }, []);

  const saveVoiceUri = (v) => {
    try {
      if (typeof window === "undefined") return;
      if (v?.voiceURI) localStorage.setItem("tts_voice_uri", v.voiceURI);
    } catch (e) {}
  };

  const pickBestKoreanVoice = () => {
    const voices = getVoicesSafe();
    if (!voices.length) return null;

    const savedUri =
      typeof window !== "undefined"
        ? localStorage.getItem("tts_voice_uri")
        : null;

    if (savedUri) {
      const found = voices.find((v) => v.voiceURI === savedUri);
      if (found) return found;
      try {
        localStorage.removeItem("tts_voice_uri");
      } catch (e) {}
    }

    const ko = voices.filter((v) =>
      String(v.lang || "").toLowerCase().startsWith("ko")
    );

    const femaleHint =
      /(female|woman|여|여성|yuna|jini|seoyeon|sunhi|sora|mina|지니|유나|서연|선희|소라|미나)/i;

    const v =
      ko.find((x) => femaleHint.test(x.name) || femaleHint.test(x.voiceURI)) ||
      ko.find((x) => x.default) ||
      (ko.length ? ko[0] : null) ||
      voices.find((x) => x.default) ||
      voices[0];

    if (v) saveVoiceUri(v);
    return v || null;
  };

  const splitForTTS = (text) => {
    const t = String(text || "").trim();
    if (!t) return [];

    const withPauses = t.replace(
      /(\s*)(그리고|또한|다만|그래서|그러면|하지만)\s*:/g,
      "\n$2:"
    );

    const lines = withPauses
      .split(/\n+/)
      .map((x) => x.trim())
      .filter(Boolean);

    const out = [];
    for (const line of lines) {
      if (out.length > 0) out.push({ __gap: "line" });

      const parts = line
        .replace(/\s+/g, " ")
        .split(/(?<=[\.\!\?\。\！\？])\s+/g)
        .map((x) => x.trim())
        .filter(Boolean);

      for (const p of parts) {
        if (p.length <= 220) out.push(p);
        else out.push(p);
      }
    }
    return out;
  };

  const TTS_RATE = 1.1;
  const TTS_PITCH = 1.05;
  const TTS_GAP_MS = 100;
  const TTS_LINE_GAP_MS = 350;

  const stopTTS = () => {
    if (isAndroidWebView()) {
      androidCall("stopTTS");
      setIsSpeaking(false);
      isSpeakingRef.current = false;
      setSpeakingIdx(null);
      return;
    }

    if (!hasTTS) return;
    try {
      window.speechSynthesis.cancel();
    } catch (e) {}
    utterRef.current = null;

    ttsQueueRef.current = [];
    ttsQueueIdxRef.current = 0;

    setIsSpeaking(false);
    setSpeakingIdx(null);
  };

  const buildTtsForReply = (replyText, attachments) => {
    let t = String(replyText || "");

    // ✅ TTS가 기호를 그대로 읽어(예: "대괄호","괄호","퍼센트","일 괄호") 어색해지는 걸 방지.
    //    화면 표시는 그대로 두고, "읽어주기"에 넘기는 텍스트만 자연스럽게 다듬는다.

    // 0) 날짜 범위 "2026-08-18 ~ 2026-08-22" -> "8월 18일부터 8월 22일까지"
    t = t.replace(
      /(\d{4})-(\d{2})-(\d{2})\s*[~∼]\s*(\d{4})-(\d{2})-(\d{2})/g,
      (m, y1, mo1, d1, y2, mo2, d2) =>
        `${parseInt(mo1, 10)}월 ${parseInt(d1, 10)}일부터 ${parseInt(mo2, 10)}월 ${parseInt(d2, 10)}일까지`
    );

    // 0-2) 단독 날짜 "2026-08-18" -> "8월 18일"
    t = t.replace(/(\d{4})-(\d{2})-(\d{2})/g, (m, y, mo, d) =>
      `${parseInt(mo, 10)}월 ${parseInt(d, 10)}일`
    );

    // 1) 날짜 대괄호 [2026-08-18] 는 위에서 이미 "8월 18일"이 되므로 대괄호만 제거됨

    // 2) 그 외 대괄호 [내용] -> 내용
    t = t.replace(/\[([^\]]*)\]/g, "$1");

    // 3) 목록 기호 제거: 줄 앞의 "- ", "* ", "• "
    t = t.replace(/^[\s]*[-*•]\s+/gm, "");

    // 4) "1) 2) 3)" / "1." 같은 순번 기호 제거
    t = t.replace(/^\s*\d+\)\s*/gm, "");
    t = t.replace(/^\s*\d+\.\s+/gm, "");
    // 순번 뒤에 다시 불릿이 남는 경우("3) - 내용") 한 번 더 제거
    t = t.replace(/^[\s]*[-*•]\s+/gm, "");
    t = t.replace(/\s[-*•]\s+/g, " ");

    // 5) 콜론 " : " -> " " (예: "불량률감소 : 1.45%" -> "불량률감소 1.45퍼센트")
    t = t.replace(/\s*:\s*/g, " ");

    // 6) 퍼센트/단위 자연스럽게
    t = t.replace(/%/g, "퍼센트");
    t = t.replace(/(\d)\s*ea\b/gi, "$1개");

    // 7) 괄호 (내용) -> ", 내용," (읽을 때 끊어읽기)
    t = t.replace(/\s*\(([^)]*)\)\s*/g, ", $1, ");

    // 8) 남은 특수기호 정리
    t = t.replace(/[#>_*~`]/g, "");

    // 9) 공백/줄바꿈 정리 — 줄바꿈은 문장 끊김으로
    t = t.replace(/[ \t]+/g, " ");
    t = t.replace(/\s*\n\s*/g, ". ");
    // 쉼표/마침표 겹침 정리
    t = t.replace(/,\s*\./g, ".");
    t = t.replace(/\.\s*\.\s*/g, ". ");
    t = t.replace(/,\s*,/g, ", ");
    t = t.replace(/\s+([.,])/g, "$1");
    t = t.replace(/\s+/g, " ").trim();

    return t;
  };

  const speakText = (text, idx = null) => {
    const raw = String(text || "").trim();
    if (!raw) return;

    if (isListening) {
      try {
        autoListenRef.current = false;
        recognitionRef.current?.abort?.();
      } catch (e) {}
      if (isAndroidWebView()) {
        try {
          androidCall("stopSTT");
        } catch (e) {}
      }
      setIsListening(false);
    }

    if (isAndroidWebView()) {
      stopTTS();
      setSpeakingIdx(idx);
      androidCall("speak", ttsNormalize(raw));
      return;
    }

    if (!hasTTS) {
      Swal.fire({
        title: '<p style="color:red">TTS 미지원</p>',
        text: "현재 브라우저에서 음성 출력(TTS)을 지원하지 않습니다.",
        confirmButtonText: "확인",
      });
      return;
    }

    const t = ttsNormalize(raw).trim();
    if (!t) return;

    stopTTS();

    try {
      try {
        window.speechSynthesis.getVoices();
      } catch (e) {}

      const voice = pickBestKoreanVoice();
      const chunks = splitForTTS(t);
      if (!chunks.length) return;

      ttsQueueRef.current = chunks;
      ttsQueueIdxRef.current = 0;

      const speakNext = () => {
        const list = ttsQueueRef.current || [];
        const i = ttsQueueIdxRef.current || 0;

        if (!list[i]) {
          setIsSpeaking(false);
          setSpeakingIdx(null);
          utterRef.current = null;
          ttsQueueRef.current = [];
          ttsQueueIdxRef.current = 0;
          return;
        }

        const item = list[i];

        if (item && typeof item === "object" && item.__gap === "line") {
          ttsQueueIdxRef.current = i + 1;
          setTimeout(speakNext, TTS_LINE_GAP_MS);
          return;
        }

        const u = new window.SpeechSynthesisUtterance(String(item));
        if (voice) u.voice = voice;
        u.lang = voice?.lang || "ko-KR";
        u.rate = TTS_RATE;
        u.pitch = TTS_PITCH;
        u.volume = 1.0;

        u.onstart = () => {
          setIsSpeaking(true);
          setSpeakingIdx(idx);
        };

        u.onend = () => {
          utterRef.current = null;
          ttsQueueIdxRef.current = i + 1;
          setTimeout(speakNext, TTS_GAP_MS);
        };

        u.onerror = () => {
          setIsSpeaking(false);
          setSpeakingIdx(null);
          utterRef.current = null;
          ttsQueueRef.current = [];
          ttsQueueIdxRef.current = 0;
        };

        utterRef.current = u;

        try {
          window.speechSynthesis.resume();
        } catch (e) {}

        window.speechSynthesis.speak(u);
      };

      setTimeout(() => {
        try {
          window.speechSynthesis.resume();
        } catch (e) {}
        speakNext();
      }, 80);
    } catch (e) {
      setIsSpeaking(false);
      setSpeakingIdx(null);
      utterRef.current = null;
      ttsQueueRef.current = [];
      ttsQueueIdxRef.current = 0;
    }
  };

  useEffect(() => {
    return () => {
      stopTTS();
      if (sttTimerRef.current) clearTimeout(sttTimerRef.current);
      if (sttSilenceRef.current) clearTimeout(sttSilenceRef.current);
      if (pendingAutoSendTimerRef.current)
        clearTimeout(pendingAutoSendTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetSttSilenceTimer = () => {
    if (!sttHasSpeechRef.current) return;
    if (sttSilenceRef.current) clearTimeout(sttSilenceRef.current);
    sttSilenceRef.current = setTimeout(() => {
      sttAutoSendRef.current = true;
      stopSTT();
    }, STT_SILENCE_MS);
  };

  const clearSttTimers = () => {
    if (sttTimerRef.current) clearTimeout(sttTimerRef.current);
    if (sttSilenceRef.current) clearTimeout(sttSilenceRef.current);
    sttTimerRef.current = null;
    sttSilenceRef.current = null;
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!enableMic) return;
    if (isAndroidWebView()) return;

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;

    const rec = new SR();
    rec.lang = "ko-KR";
    rec.continuous = true;
    rec.interimResults = true;
    // ✅ 오인식 대비 대안 후보 확보(후처리 보정에 사용)
    rec.maxAlternatives = 3;

    rec.onresult = (event) => {
      // ✅ [구조 수정] Web Speech API의 event.results는 "누적된 전체 결과"를 담는다.
      //    이전처럼 sttFinalRef에 조각을 이어붙이면(+=), 브라우저가 같은 구간을
      //    여러 번 final로 보낼 때 "KPIKPI", "7월7월" 같은 반복이 생긴다.
      //    → 누적하지 말고, 매번 results 전체를 0부터 다시 조립한다.
      let finalText = "";
      let interim = "";

      for (let i = 0; i < event.results.length; i += 1) {
        const r = event.results[i];
        const t = (r?.[0]?.transcript || "").trim();
        if (!t) continue;
        if (r.isFinal) {
          finalText += (finalText ? " " : "") + t;
        } else {
          interim += (interim ? " " : "") + t;
        }
      }

      // 확정분은 ref에 "덮어쓰기"(누적 X) — 반복이 구조적으로 생기지 않음
      sttFinalRef.current = finalText;

      if (finalText || interim) {
        sttHasSpeechRef.current = true;
        resetSttSilenceTimer();
      }

      // 표시용: 확정 + 임시 (안전망으로 cleanSttText 유지)
      const merged = cleanSttText(
        (finalText + " " + interim).replace(/\s+/g, " ")
      );
      setSttDraft(merged);
    };

    rec.onerror = () => {
      clearSttTimers();
      sttTimerRef.current = setTimeout(() => {
        setIsListening(false);

        const finalTextRaw = cleanSttText(sttFinalRef.current || "");
        const draftTextRaw = cleanSttText(sttDraftRef.current || "");
        const finalText = finalTextRaw || draftTextRaw;

        const base = (sttBaseInputRef.current || "").trim();
        const combined = cleanSttText([base, finalText].filter(Boolean).join(" "));

        setInput(combined);
        setSttDraft("");

        // ✅ 세션 확정 텍스트를 input에 넘겼으므로 누적 버퍼를 비운다
        //    (비우지 않으면 다음 재시작 때 이전 확정분이 다시 붙어 글자가 반복됨)
        sttFinalRef.current = "";

        sttAutoSendRef.current = false;
        sttHasSpeechRef.current = false;

        const shouldRestart =
          autoListenRef.current &&
          !locked &&
          !isLoading &&
          !isSpeaking &&
          String(inputStateRef.current || "").trim() === "";

        if (shouldRestart) {
          // ✅ 재시작 직전에도 확정 버퍼를 확실히 비워둔다
          sttFinalRef.current = "";
          sttBaseInputRef.current = "";
          setTimeout(() => {
            try {
              rec.start();
              setIsListening(true);
            } catch (e) {}
          }, 300);
        }
      }, 200);
    };

    rec.onend = () => {
      clearSttTimers();
      sttTimerRef.current = setTimeout(() => {
        setIsListening(false);

        const finalTextRaw = cleanSttText(sttFinalRef.current || "");
        const draftTextRaw = cleanSttText(sttDraftRef.current || "");
        const finalText = finalTextRaw || draftTextRaw;

        const base = (sttBaseInputRef.current || "").trim();
        const combined = cleanSttText([base, finalText].filter(Boolean).join(" "));

        setInput(combined);
        setSttDraft("");

        // ✅ 확정 텍스트를 input으로 넘겼으니 누적 버퍼 비움(반복 방지 핵심)
        sttFinalRef.current = "";

        if (sttAutoSendRef.current && finalText) {
          sttAutoSendRef.current = false;
          pendingAutoSendTextRef.current = combined;
          if (pendingAutoSendTimerRef.current)
            clearTimeout(pendingAutoSendTimerRef.current);

          pendingAutoSendTimerRef.current = setTimeout(() => {
            const cur = String(inputStateRef.current || "").trim();
            const target = String(pendingAutoSendTextRef.current || "").trim();
            if (cur && target && cur === target && !isLoading && !locked) {
              handleSendText(cur, true);
            }
          }, STT_SEND_DELAY_MS);
        } else {
          sttAutoSendRef.current = false;
        }

        sttHasSpeechRef.current = false;

        const shouldRestart =
          autoListenRef.current &&
          !locked &&
          !isLoading &&
          !isSpeaking &&
          String(inputStateRef.current || "").trim() === "";

        if (shouldRestart) {
          // ✅ 재시작 사이클에서 이전 확정분이 다시 붙지 않도록 버퍼 초기화
          sttFinalRef.current = "";
          sttBaseInputRef.current = "";
          setTimeout(() => {
            try {
              rec.start();
              setIsListening(true);
            } catch (e) {}
          }, 300);
        }
      }, 200);
    };

    recognitionRef.current = rec;

    return () => {
      try {
        autoListenRef.current = false;
        rec.abort();
      } catch (e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableMic]);

  const formatDateTime = (ts) => {
    if (!ts) return "";
    try {
      const d = new Date(ts);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const hh = String(d.getHours()).padStart(2, "0");
      const mi = String(d.getMinutes()).padStart(2, "0");
      const ss = String(d.getSeconds()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
    } catch {
      return "";
    }
  };

  const buildHistoryForServer = (msgs) =>
    msgs
      .filter((m) => m.role === "user" || m.role === "assistant")
      .slice(-HISTORY_MAX)
      .map((m) => ({ role: m.role, content: m.content }));

  const normalizeServerPath = (p) => {
    if (!p) return "";
    let s = String(p);
    s = s.replaceAll("\\\\", "\\");
    s = s.replaceAll("/", "\\");
    s = s.replace(/^\\+/, "");
    s = s.replaceAll("..", "");
    return s;
  };

  const extractFileName = (fileName, filePath) => {
    if (fileName && String(fileName).trim()) return String(fileName).trim();
    const p = String(filePath || "");
    const last = p.split(/[/\\]/).pop();
    return last || "download";
  };

  const uploadOrderExcelFile = async (file) => {
    if (!file) throw new Error("파일이 없습니다.");

    const name = (file.name || "").toLowerCase();
    if (!name.endsWith(".xlsx") && !name.endsWith(".xlsm")) {
      throw new Error("템플릿 Excel 파일만 업로드 가능합니다.");
    }

    const form = new FormData();
    form.append("file", file); // ⭐ 서버 @RequestParam("file") 고정

    const res = await axios.post("/api/p/ExcelUpload/file", form, {
      withCredentials: true,
    });

    return res.data;
  };

  // ✅ 첨부 버튼(플러스 아이콘) 클릭 시, 파일선택 대신 메뉴 팝업 열기
  const handleAttachClick = (e) => {
    if (isMobileChat) return;
    if (locked || isLoading || isListening || isSpeaking || isUploadingFile) return;
    setAttachMenuAnchor(e.currentTarget);
  };

  // ✅ [추가] 첨부 메뉴 닫기 함수
  const closeAttachMenu = () => {
    setAttachMenuAnchor(null);
  };

  // ✅ [추가] 메뉴 항목: "수주템플릿 다운로드"
  const handleMenuTemplateDownload = () => {
    closeAttachMenu();
    
    try {
      // 💡 파일이 위치한 프론트엔드 경로 (public 폴더 기준)
      // 예: public/Files/수주정보.xlsx 에 넣었다면 아래와 같이 작성
      const fileUrl = '/Excel.zip'; 
      
      const link = document.createElement('a');
      link.href = fileUrl;
      link.setAttribute('download', '엑셀 템플릿.zip'); // 다운로드될 때의 파일명
      document.body.appendChild(link);
      link.click();
      link.remove();
      
    } catch (err) {
      console.error("템플릿 다운로드 에러:", err);
      Swal.fire({ 
        title: '<p style="color:red">다운로드 실패</p>', 
        text: '템플릿을 찾을 수 없거나 다운로드에 실패했습니다.', 
        confirmButtonText: '확인' 
      });
    }
  };

  // ✅ [추가] 메뉴 항목: "엑셀 업로드"
  const handleMenuUploadClick = () => {
    closeAttachMenu();
    fileInputRef.current?.click();
  };

  // ✅ 엑셀만 받는다.
  //
  //    input 의 accept 속성은 '파일 선택 창에 보여줄 것'만 거른다.
  //    끌어다 놓기(drag&drop)는 accept 를 전혀 안 거치고,
  //    파일 선택 창에서도 '모든 파일'을 고르면 뚫린다.
  //    그래서 사진을 끌어다 놓으면 그대로 엑셀 업로드 API 로 날아갔고,
  //    서버가 파싱에 실패해 "엑셀 양식이 맞지 않습니다 / 템플릿 받으시겠습니까"
  //    라는 엉뚱한 안내가 떴다. 큰 동영상이면 그게 통째로 서버까지 올라간다.
  const EXCEL_EXTS = [".xlsx", ".xlsm"];

  const isExcelFile = (file) => {
    const name = String(file?.name || "").toLowerCase();
    return EXCEL_EXTS.some((ext) => name.endsWith(ext));
  };

  const processExcelUpload = async (file, source) => {
    if (!file) return;
    if (locked || isLoading || isListening || isSpeaking || isUploadingFile) return;

    if (!isExcelFile(file)) {
      Swal.fire({
        title: "엑셀 파일만 올릴 수 있습니다",
        html:
          `올리신 파일: <b>${String(file?.name || "알 수 없음")}</b><br/><br/>` +
          `수주·발주·입고·출하 엑셀(<b>.xlsx</b>, <b>.xlsm</b>)만 처리할 수 있습니다.` +
          `<br/><br/><span style="color:gray; font-size:13px;">` +
          `사진이나 문서는 읽지 못합니다. 궁금한 내용은 글로 물어봐 주세요.</span>`,
        confirmButtonText: "확인",
      });
      return;
    }

    if (appStore.loading) return;
    appStore.setLoading(true);

    try {
      setIsUploadingFile(true);
      const data = await uploadOrderExcelFile(file);
      
      // 백엔드가 주는 data.type 사용
      const uploadType = data?.type ? String(data.type).toLowerCase() : "";
      const payload = { from: source, fileName: file?.name || "", uploadResult: data };
      
      switch (uploadType) {
        case "order":
          //Swal.fire({ title: "업로드 완료", text: "수주 엑셀 업로드가 완료되었습니다.", confirmButtonText: "확인" });
          openOrderReg(payload);
          break;
        case "purchase":
          //Swal.fire({ title: "업로드 완료", text: "발주 엑셀 업로드가 완료되었습니다.", confirmButtonText: "확인" });
          openPurchaseReg(payload);
          break;
        case "incoming":
          //Swal.fire({ title: "업로드 완료", text: "입고 엑셀 업로드가 완료되었습니다.", confirmButtonText: "확인" });
          openIncomingReg(payload);
          break;
        case "outgoing":
          //Swal.fire({ title: "업로드 완료", text: "출하 엑셀 업로드가 완료되었습니다.", confirmButtonText: "확인" });
          openOutgoingReg(payload);
          break;
        default:
          throw new Error("MES에 등록되지 않은 엑셀 템플릿입니다.");
      }
      
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || err?.message || "업로드 실패";

      Swal.fire({
        title: '<p style="color:red">엑셀 양식 오류</p>',
        html: `업로드하신 엑셀 파일의 양식이 맞지 않거나 데이터를 읽을 수 없습니다.<br/><br/><span style="color:gray; font-size:13px;">(상세오류: ${String(msg)})</span><br/><br/><b>표준 엑셀 템플릿을 다운로드 하시겠습니까?</b>`,
        showCancelButton: true,
        confirmButtonColor: '#10b981',
        cancelButtonColor: '#6b7280',
        confirmButtonText: '예, 양식 다운로드',
        cancelButtonText: '닫기'
      }).then((result) => {
        if (result.isConfirmed) handleMenuTemplateDownload();
      });
    } finally {
      setIsUploadingFile(false);
      appStore.setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // 파일 선택창 초기화
    if (file) {
      processExcelUpload(file, 'excel_upload');
    }
  };

  const tryUploadFromDragEvent = (evt) => {
    const dt = evt?.dataTransfer;
    const files = dt?.files;

    // 폴더를 끌어다 놓으면 files 가 비어 있다. 아무 말 없이 사라지면
    // 사용자는 업로드가 된 줄 안다.
    if (!files || files.length === 0) {
      Swal.fire({
        title: "파일을 읽지 못했습니다",
        html:
          `폴더는 올릴 수 없습니다. 엑셀 파일(<b>.xlsx</b>, <b>.xlsm</b>)을 ` +
          `직접 끌어다 놓아 주세요.`,
        confirmButtonText: "확인",
      });
      return;
    }

    // 한 번에 한 개만 처리한다(기존 동작). 여러 개면 그 사실을 알린다.
    if (files.length > 1) {
      Swal.fire({
        title: "한 번에 한 개만 올릴 수 있습니다",
        html:
          `${files.length}개를 올리셨습니다. 첫 번째 파일 ` +
          `<b>${String(files[0]?.name || "")}</b> 만 처리할까요?`,
        showCancelButton: true,
        confirmButtonText: "네, 진행",
        cancelButtonText: "취소",
      }).then((r) => {
        if (r.isConfirmed) processExcelUpload(files[0], "excel_dragdrop");
      });
      return;
    }

    processExcelUpload(files[0], "excel_dragdrop");
  };

  const startSTT = () => {
    if (!enableMic) return;
    if (locked || isLoading) return;
    if (isSpeaking) return;

    autoListenRef.current = false;

    sttUsedRef.current = true;
    sttAutoSendRef.current = false;
    sttHasSpeechRef.current = false;

    sttBaseInputRef.current = inputStateRef.current || "";

    setSttDraft("");
    sttFinalRef.current = "";
    setIsListening(true);

    if (isAndroidWebView()) {
      const ok = androidCall("startSTT");
      if (!ok) {
        setIsListening(false);
        Swal.fire({
          title: '<p style="color:red">STT 실패</p>',
          text: "Android STT 브릿지 호출에 실패했습니다.",
          confirmButtonText: "확인",
        });
      }
      return;
    }

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = recognitionRef.current;

    if (!SR || !rec) {
      Swal.fire({
        title: '<p style="color:red">STT 미지원</p>',
        text:
          "현재 환경(WebView/브라우저)에서 웹 STT(Web Speech API)를 지원하지 않습니다.\n" +
          "※ 이 경우 안드로이드 앱 네이티브 STT로 브릿지해야 합니다.",
        confirmButtonText: "확인",
      });
      setIsListening(false);
      return;
    }

    try {
      rec.start();
    } catch (e) {
      setIsListening(false);
    }
  };

  const stopSTT = () => {
    if (!enableMic) return;

    autoListenRef.current = false;

    if (isAndroidWebView()) {
      androidCall("stopSTT");
      setIsListening(false);
      return;
    }

    const rec = recognitionRef.current;
    if (!rec) return;

    try {
      rec.stop();
    } catch (e) {}

    setTimeout(() => {
      try {
        rec.abort();
      } catch (e) {}
    }, 700);
  };

  const toggleSTT = () => {
    if (!enableMic) return;

    if (isListening) {
      sttAutoSendRef.current = false;
      stopSTT();
    } else startSTT();
  };

  const downloadFile = async (
    attachment,
    {
      url = "/api/SaveCommon/FileDownload",
      fileNameKey = "file_name",
      serverPathKey = "file_name_sever",
      serverParamName = "fileNameSever",
      fileParamName = "fileName",
    } = {}
  ) => {
    if (!attachment) return;

    const rawName = attachment?.[fileNameKey];
    const rawServer = attachment?.[serverPathKey];

    const finalName = extractFileName(rawName, rawServer);
    const serverPath = normalizeServerPath(rawServer);

    if (!serverPath) {
      console.log("다운로드 파라미터 부족:", attachment);
      return;
    }

    try {
      const response = await axios.post(
        url,
        new URLSearchParams({
          [fileParamName]: finalName,
          [serverParamName]: serverPath,
        }),
        { withCredentials: true, responseType: "blob" }
      );

      if (response.status === 200) {
        const blob = new Blob([response.data]);
        const dlUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = dlUrl;
        link.setAttribute("download", finalName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(dlUrl);
      }
    } catch (error) {
      console.log("네트워크 실패", error);
    }
  };

  // ✅ 대화 초기화(복원 저장분까지 삭제) — 필요 시 헤더 버튼 등에 연결해서 사용
  const handleClearChat = () => {
    try {
      sessionStorage.removeItem(messagesStorageKey);
    } catch (e) {}
    setMessages([buildGreeting()]);
    setLastSqlKey(null);
    setLastToolParams({});
    setLastToolRowsPreview([]);
  };

  const handleSendText = async (rawText, fromMic = false) => {
    cancelPendingAutoSend();
    const text = String(rawText || "").trim();
    if (!text) return;
    if (isLoading) return;
    if (locked) return;
    if (isListening) return;

    //  클라이언트 가드는 '쓸데없는 요청을 줄이는' 용도일 뿐이다.
    //  뚫려도 서버(ai_quota.consume_quota)가 막는다.
    if (!quotaUnlimited && dailyLimit > 0 && askCount >= dailyLimit) {
      setLocked(true);
      return;
    }

    lastSendByMicRef.current = !!fromMic || !!sttUsedRef.current;
    sttUsedRef.current = false;

    const now = Date.now();

    const nextMessages = [
      ...messages,
      { role: "user", content: text, ts: now, attachments: [] },
    ];
    setMessages(nextMessages);

    setInput("");
    setIsLoading(true);

    const nextAskCount = askCount + 1;
    setAskCount(nextAskCount);

    try {
      const res = await axios.post(
        API_URL,
        {
          message: text,
          page_url: window.location.href,
          company_id: companyId,
          history: buildHistoryForServer(nextMessages),
          sticky_gpt_remaining: stickyLeft,
          last_sql_key: lastSqlKey,
          last_tool_params: lastToolParams,
          last_tool_rows_preview: lastToolRowsPreview,
        },
        {
          timeout: 60000,
          //  ✅ 신원 토큰. 챗봇은 이 값으로 company_id/user_id 를 결정하고,
          //     요청 본문의 company_id 는 무시한다(위조 방지).
          headers: chatTokenRef.current
            ? { "X-DIFF-CHAT-TOKEN": chatTokenRef.current }
            : undefined,
        }
      );

      const botReply = res.data?.response ?? "응답을 받지 못했습니다.";
      const sticky = res.data?.sticky_gpt_remaining ?? 0;

      const attachments = Array.isArray(res.data?.attachments)
        ? res.data.attachments
        : [];

      setStickyLeft(sticky);

      setLastSqlKey(res.data?.last_sql_key ?? null);
      setLastToolParams(res.data?.last_tool_params ?? {});
      setLastToolRowsPreview(res.data?.last_tool_rows_preview ?? []);

      const afterBot = [
        ...nextMessages,
        {
          role: "assistant",
          content: botReply,
          ts: Date.now(),
          attachments,
        },
      ];

      //  서버가 내려준 사용량이 있으면 그 값이 진짜다.
      const sq = res.data?.quota;
      if (sq && typeof sq.limit === "number") {
        setDailyLimit(sq.limit);
        setQuotaUnlimited(sq.limit < 0);
        if (typeof sq.used === "number") {
          setAskCount(sq.used);
        }
      }
      if (res.data?.action === "quota_exceeded") {
        setLocked(true);
      } else if (!quotaUnlimited && dailyLimit > 0 && nextAskCount >= dailyLimit) {
        setLocked(true);
      }

      setMessages(afterBot);

      if (lastSendByMicRef.current) {
        lastSendByMicRef.current = false;
        const ttsText = buildTtsForReply(botReply, attachments);
        speakText(ttsText, -1);
      }
    } catch (err) {
      console.error(err);

      // ✅ 서버 호출 실패는 사용횟수에서 차감(롤백)하지 않는 것이 합리적
      setAskCount((c) => (c > 0 ? c - 1 : 0));

      const errMsgs = [
        ...nextMessages,
        {
          role: "error",
          content: "서버 호출 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.",
          ts: Date.now(),
          attachments: [],
        },
      ];

      setMessages(errMsgs);
    } finally {
      setIsLoading(false);
    }
  };
  handleSendTextRef.current = handleSendText;

  const handleSend = async () => {
    const text = input.trim();
    return handleSendText(text, false);
  };

  const handleKeyDown = (e) => {
    if (locked || isLoading || isListening) {
      e.preventDefault();
      return;
    }

    if (e.key === "Enter") {
      if (!e.shiftKey && !e.nativeEvent.isComposing) {
        e.preventDefault();
        handleSend();
      }
    }
  };

  const hasText = input.trim().length > 0;
  const sendButtonStyles = {
    width: 40,
    height: 40,
    borderRadius: 999,
    ...(hasText
      ? {
          backgroundColor: "#3b82f6",
          color: "#fff",
          "&:hover": { backgroundColor: "#2563eb" },
        }
      : {}),
  };

  const handleRightAction = () => {
    if (isSpeaking) {
      stopTTS();
      return;
    }

    if (hasText) {
      handleSend();
      return;
    }

    if (enableMic) {
      toggleSTT();
    } else {
      handleAttachClick();
    }
  };

  const chatBody = (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
          gap: 0,
          paddingTop: 4,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {locked && (
          <div
            style={{
              marginBottom: 2,
              padding: "10px 12px",
              borderRadius: 10,
              backgroundColor: "#fee2e2",
              border: "1px solid #ef4444",
              color: "#991b1b",
              fontSize: 13,
              fontWeight: 700,
              flex: "0 0 auto",
            }}
          >
            더 이상 질문할 수 없습니다. (오늘 제한 {dailyLimit > 0 ? dailyLimit : DAILY_LIMIT_FALLBACK}회)
          </div>
        )}

        {/* 대화 영역 */}
        <div
          ref={listRef}
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
            border: "1px solid #ddd",
            borderRadius: 10,
            padding: "5px 12px",
            backgroundColor: "#fafafa",
          }}
        >
          {messages.map((m, idx) => {
            const isUser = m.role === "user";
            const isError = m.role === "error";
            const isAssistant = m.role === "assistant";

            const bubbleBg = isError ? "#ef4444" : isUser ? "#1976d2" : "white";
            const bubbleColor = isError || isUser ? "white" : "black";
            const bubbleBorder = isError ? "1px solid #ef4444" : "1px solid #ddd";

            const atts = Array.isArray(m.attachments) ? m.attachments : [];
            const orderFiles = atts.filter((a) => a?.type === "order_file");

            const canSpeakThis =
              isAssistant && !isUser && !isError && String(m.content || "").trim();

            const isThisSpeaking = isSpeaking && speakingIdx === idx;

            // ✅ 인덱스 단독 키 대신 ts+role+idx 복합키(재사용/섞임 방지)
            const rowKey = `${m.ts || 0}_${m.role || "x"}_${idx}`;

            return (
              <div
                key={rowKey}
                style={{
                  marginBottom: 10,
                  textAlign: isUser ? "right" : "left",
                }}
              >
                <div
                  style={{
                    display: "inline-block",
                    padding: "8px 12px",
                    borderRadius: 10,
                    backgroundColor: bubbleBg,
                    color: bubbleColor,
                    border: bubbleBorder,
                    maxWidth: "90%",
                    whiteSpace: "pre-wrap",
                    textAlign: "left",
                    fontSize: 14,
                    lineHeight: 1.45,
                  }}
                >
                  {m.content}
                </div>

                {!isUser && !isError && orderFiles.length > 0 && (
                  <div
                    style={{
                      marginTop: 8,
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                    }}
                  >
                    {orderFiles.map((f, i) => {
                      const name = extractFileName(f.file_name, f.file_name_sever);
                      const fileKey =
                        f.order_file_id ?? f.file_name_sever ?? `${name}_${i}`;

                      return (
                        <div
                          key={fileKey}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            maxWidth: "90%",
                            color: "#374151",
                            fontSize: 13,
                          }}
                        >
                          <span
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={name}
                          >
                            {name}
                            {f.size_dp ? ` (${f.size_dp})` : ""}
                          </span>

                          <IconButton
                            size="small"
                            onClick={() => downloadFile(f)}
                            sx={{
                              padding: "2px",
                              border: "1px solid #e5e7eb",
                              borderRadius: "8px",
                              backgroundColor: "#fff",
                              "&:hover": { backgroundColor: "#f3f4f6" },
                            }}
                            title="다운로드"
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div
                  style={{
                    marginTop: 4,
                    fontSize: 11,
                    color: "#6b7280",
                    textAlign: isUser ? "right" : "left",
                    paddingLeft: isUser ? 0 : 2,
                    paddingRight: isUser ? 2 : 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    justifyContent: isUser ? "flex-end" : "flex-start",
                  }}
                >
                  <span>{formatDateTime(m.ts)}</span>

                  {canSpeakThis && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (isThisSpeaking) stopTTS();
                        else speakText(m.content, idx);
                      }}
                      sx={{
                        padding: "2px",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        "&:hover": { backgroundColor: "#f3f4f6" },
                      }}
                      title={isThisSpeaking ? "읽기 중지" : "읽어주기"}
                    >
                      {isThisSpeaking ? (
                        <StopCircleIcon fontSize="small" />
                      ) : (
                        <VolumeUpIcon fontSize="small" />
                      )}
                    </IconButton>
                  )}
                </div>
              </div>
            );
          })}

          {isLoading && (
            <div style={{ display: "flex", justifyContent: "flex-start" }}>
              <div
                style={{
                  padding: "8px 12px",
                  borderRadius: 10,
                  backgroundColor: "#fafafa",
                  fontSize: 12,
                  border: "1px solid #ddd",
                }}
              >
                생각 중입니다...
              </div>
            </div>
          )}
        </div>

        {/* ✅ 입력 영역 */}
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            paddingTop: 6,
            paddingBottom: isAndroidWebView() ? 6 : "max(env(safe-area-inset-bottom), 6px)",
            flex: "0 0 auto",
          }}
        >
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 999,
              paddingLeft: 10,
              paddingRight: 10,
              minHeight: 52,
              boxSizing: "border-box",
            }}
          >
            {/* PC에서만 첨부 버튼 및 팝업 메뉴 */}
            {!isMobileChat && (
              <>
                <IconButton
                  aria-label="첨부"
                  onClick={handleAttachClick}
                  disabled={locked || isLoading || isListening || isSpeaking || isUploadingFile}
                  sx={{ width: 40, height: 40, borderRadius: 999 }}
                >
                  <span style={{ fontSize: 22, lineHeight: 1 }}>＋</span>
                </IconButton>
                
                {/* ✅ [추가] 첨부 메뉴 팝업 (템플릿 다운로드 / 엑셀 업로드) */}
                <Menu
                  anchorEl={attachMenuAnchor}
                  open={attachMenuOpen}
                  onClose={closeAttachMenu}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  PaperProps={{
                    elevation: 0,
                    sx: {
                      overflow: 'visible',
                      filter: 'drop-shadow(0px 4px 12px rgba(0,0,0,0.1))',
                      mt: -1, // 메뉴 뜨는 위치 살짝 위로 조정
                      borderRadius: '12px', // 둥근 모서리
                      border: '1px solid #e5e7eb',
                      minWidth: '180px',
                      padding: '4px 0'
                    },
                  }}
                >
                  {/* 다운로드 버튼 */}
                  <MenuItem 
                    onClick={handleMenuTemplateDownload}
                    sx={{
                      fontSize: '14px',
                      fontWeight: 700,
                      color: '#374151',
                      padding: '12px 18px',
                      transition: 'all 0.2s ease',
                      '&:hover': { backgroundColor: '#f0f9ff', color: '#2563eb' } // 마우스 올리면 파란색 톤
                    }}
                  >
                    <DownloadIcon sx={{ fontSize: 20, mr: 1.5, color: 'inherit' }} />
                    템플릿 다운로드
                  </MenuItem>

                  {/* 업로드 버튼 */}
                  <MenuItem 
                    onClick={handleMenuUploadClick}
                    sx={{
                      fontSize: '14px',
                      fontWeight: 700,
                      color: '#374151',
                      padding: '12px 18px',
                      transition: 'all 0.2s ease',
                      '&:hover': { backgroundColor: '#f0fdf4', color: '#10b981' } // 마우스 올리면 초록색 톤
                    }}
                  >
                    <AttachFileIcon sx={{ fontSize: 20, mr: 1.5, color: 'inherit' }} />
                    엑셀 파일 업로드
                  </MenuItem>
                </Menu>
              </>
            )}

            {!isMobileChat && (
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
            )}

            <textarea
              ref={inputRef}
              placeholder={
                locked
                  ? "더 이상 질문할 수 없습니다."
                  : isListening
                  ? "듣는 중..."
                  : "무엇이든 물어보세요"
              }
              value={
                isListening && !isAndroidWebView()
                  ? [sttBaseInputRef.current, (sttDraft || "").trim() ? sttDraft : ""]
                      .filter(Boolean)
                      .join(" ")
                  : input
              }
              onChange={(e) => {
                if (!isListening) {
                  sttUsedRef.current = false;
                  setInput(e.target.value);
                }
              }}
              onKeyDown={handleKeyDown}
              disabled={locked || isLoading || isListening}
              rows={1}
              style={{
                flex: 1,
                resize: "none",
                border: "none",
                outline: "none",
                fontSize: 16,
                padding: "10px 8px",
                background: "transparent",
                lineHeight: "22px",
                maxHeight: 140,
              }}
            />

            {/* 모바일에서만 마이크 */}
            {enableMic && (
              <IconButton
                aria-label={isListening ? "정지" : isSpeaking ? "읽기중지" : "마이크"}
                onClick={() => {
                  if (isSpeaking) {
                    stopTTS();
                    return;
                  }
                  if (isListening) {
                    stopSTT();
                  } else {
                    startSTT();
                  }
                }}
                disabled={locked || isLoading}
                sx={{ width: 40, height: 40, borderRadius: 999 }}
              >
                {isSpeaking ? (
                  <StopIcon fontSize="small" />
                ) : isListening ? (
                  <StopIcon fontSize="small" />
                ) : (
                  <MicIcon fontSize="small" />
                )}
              </IconButton>
            )}

            <IconButton
              aria-label="전송"
              onClick={() => {
                if (hasText) handleSendText(input.trim(), false);
              }}
              disabled={!hasText || locked || isLoading || isListening}
              sx={sendButtonStyles}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  );

  // ✅ Full 모드
  if (shouldFull) {
    return (
      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          top: topOffset,
          height: `calc(var(--app-height, 100dvh) - ${topOffset}px)`,
          background: "#f9fafb",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            width: "min(960px, 100%)",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            padding: "0 12px",
            boxSizing: "border-box",
            minHeight: 0,
            overflow: "hidden",
            margin: "0 auto",
          }}
        >
          <div
            style={{
              flex: "0 0 auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              paddingTop: 5,
              paddingBottom: 6,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                fontWeight: 700,
                fontSize: 20,
                minWidth: 0,
              }}
            >
              <span
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {companyName} MES AI 챗봇
              </span>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {geoLabel && (
                <div style={geoLabelBoxStyle}>
                  <span style={geoLabelStyle}>{geoLabel}</span>
                  {geoFull && (
                    <Tooltip
                      title={geoFull}
                      arrow
                      open={geoTipOpen}
                      onClose={closeGeoTip}
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                    >
                      <IconButton
                        size="small"
                        aria-label="주소 보기"
                        onClick={openGeoTip}
                        sx={locationIconStyle}
                      >
                        <span style={{ fontSize: 16, lineHeight: 1 }}>📍</span>
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              )}
              <IconButton
                size="small"
                aria-label="닫기"
                onClick={chatModalClose}
                sx={closeButtonStyle}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          </div>

          <div style={{ flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}>
            {chatBody}
          </div>
          
          {/* ✅ 수주 등록 모달 (Full 화면 모드 시) */}
          {orderRegOpen && (
            <OrderRegModal
              title={"수주 등록"}
              onClose={closeOrderReg}
              userTypeModalOption={userTypeModalOption}
              unitCurrencyModalOption={unitCurrencyModalOption}
              payload={orderRegPayload}
              MenuUpdateYN={orderMenuUpdateYN}
              handleRegSuccessSave={handleRegSuccessSave} // ✅ 이 줄 추가!
            />
          )}
          {purchaseRegOpen && (
            <PurchaseRegModal
              title={"발주 등록"}
              onClose={closePurchaseReg}
              userTypeModalOption={userTypeModalOption}
              unitCurrencyModalOption={unitCurrencyModalOption}
              payload={purchaseRegPayload}
              MenuUpdateYN={purchaseMenuUpdateYN}
              handleRegSuccessSave={handlePurchaseRegSuccessSave}
            />
          )}

          {incomingRegOpen && (
            <IncomingRegModal
              title={"입고 등록"}
              onClose={closeIncomingReg}
              userTypeModalOption={userTypeModalOption}
              qualityYnModalOption={qualityYnModalOption} // 세팅한 검사여부 옵션 전달
              payload={incomingRegPayload} // 엑셀 데이터 전달!
              MenuUpdateYN={incomingMenuUpdateYN}
              handleRegSuccessSave={handleIncomingRegSuccessSave}
            />
          )}

          {outgoingRegOpen && (
            <OutgoingRegModal
              title={"출하 등록"}
              onClose={closeOutgoingReg}
              userTypeModalOption={userTypeModalOption}
              qualityYnModalOption={qualityYnModalOption} // 출하 모달도 받도록 설정되어 있다면 전달
              payload={outgoingRegPayload} // 엑셀 데이터 전달!
              MenuUpdateYN={outgoingMenuUpdateYN}
              handleRegSuccessSave={handleOutgoingRegSuccessSave}
            />
          )}
        </div>
      </div>
    );
  }

  // ✅ PC 모달
  return (
    <PCModal
      title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              width: "100%",
            }}
          >
          <div style={{ fontWeight: 700, fontSize: 18, minWidth: 0 }}>
            <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {companyName} MES AI 챗봇
            </span>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              minWidth: 0,
              gap: 8,
            }}
          >
            <div style={geoLabelStyle}>{geoLabel ? geoLabel : ""}</div>
            {geoFull ? (
              <Tooltip
                title={geoFull}
                arrow
                open={geoTipOpen}
                onClose={closeGeoTip}
                disableFocusListener
                disableHoverListener
                disableTouchListener
              >
                <IconButton
                  size="small"
                  aria-label="주소"
                  onClick={openGeoTip}
                  sx={locationIconStyle}
                >
                  <span style={{ fontSize: 15, lineHeight: 1 }}>📍</span>
                </IconButton>
              </Tooltip>
            ) : null}
            <IconButton
              size="small"
              aria-label="닫기"
              onClick={chatModalClose}
              sx={closeButtonStyle}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </div>
      }
      onClose={chatModalClose}
    >
      <div
        onDragEnter={(e) => {
          if (isMobileChat) return;
          e.preventDefault();
          e.stopPropagation();
          dragCounterRef.current += 1;
          setIsDragOver(true);
        }}
        onDragOver={(e) => {
          if (isMobileChat) return;
          e.preventDefault();
          e.stopPropagation();
          if (!isDragOver) setIsDragOver(true);
        }}
        onDragLeave={(e) => {
          if (isMobileChat) return;
          e.preventDefault();
          e.stopPropagation();
          dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
          if (dragCounterRef.current === 0) setIsDragOver(false);
        }}
        onDrop={async (e) => {
          if (isMobileChat) return;
          e.preventDefault();
          e.stopPropagation();
          dragCounterRef.current = 0;
          setIsDragOver(false);
          await tryUploadFromDragEvent(e);
        }}
        style={{
          width: 560,
          height: "min(80vh, 720px)",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          position: "relative",
        }}
      >
        {!isMobileChat && isDragOver && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 50,
              background: "rgba(255,255,255,0.85)",
              border: "2px dashed #9ca3af",
              borderRadius: 12,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              color: "#374151",
              fontWeight: 800,
              fontSize: 16,
            }}
          >
            엑셀 파일 업로드
          </div>
        )}
        <div style={{ flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}>
          {chatBody}
        </div>
        
        {/* ✅ 수주 등록 모달 (PC 모달 창 모드 시) */}
        {orderRegOpen && (
          <OrderRegModal
            title={"수주 등록"}
            onClose={closeOrderReg}
            userTypeModalOption={userTypeModalOption}
            unitCurrencyModalOption={unitCurrencyModalOption}
            payload={orderRegPayload}
            MenuUpdateYN={orderMenuUpdateYN}
            handleRegSuccessSave={handleRegSuccessSave} 
          />
        )}
        {purchaseRegOpen && (
            <PurchaseRegModal
              title={"발주 등록"}
              onClose={closePurchaseReg}
              userTypeModalOption={userTypeModalOption}
              unitCurrencyModalOption={unitCurrencyModalOption}
              payload={purchaseRegPayload}
              MenuUpdateYN={purchaseMenuUpdateYN}
              handleRegSuccessSave={handlePurchaseRegSuccessSave}
            />
          )}

        {incomingRegOpen && (
          <IncomingRegModal
            title={"입고 등록"}
            onClose={closeIncomingReg}
            userTypeModalOption={userTypeModalOption}
            qualityYnModalOption={qualityYnModalOption} // 세팅한 검사여부 옵션 전달
            payload={incomingRegPayload} // 엑셀 데이터 전달!
            MenuUpdateYN={incomingMenuUpdateYN}
            handleRegSuccessSave={handleIncomingRegSuccessSave}
          />
        )}

        {outgoingRegOpen && (
          <OutgoingRegModal
            title={"출하 등록"}
            onClose={closeOutgoingReg}
            userTypeModalOption={userTypeModalOption}
            qualityYnModalOption={qualityYnModalOption} // 출하 모달도 받도록 설정되어 있다면 전달
            payload={outgoingRegPayload} // 엑셀 데이터 전달!
            MenuUpdateYN={outgoingMenuUpdateYN}
            handleRegSuccessSave={handleOutgoingRegSuccessSave}
          />
        )}
      </div>
    </PCModal>
  );
} 

export default ChatModal;